import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Grid,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  Tabs,
  Tab,
  Typography,
  Box,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Input
} from '@mui/material';
import ContactsIcon from '@mui/icons-material/Contacts';
import DeleteIcon from '@mui/icons-material/Delete';
import HealthIcon from '@mui/icons-material/LocalHospital';
import InfoIcon from '@mui/icons-material/Info';
import LocationIcon from '@mui/icons-material/LocationOn';
import BackIcon from '@mui/icons-material/NavigateBefore';
import NextIcon from '@mui/icons-material/NavigateNext';
import PersonIcon from '@mui/icons-material/Person';
import SaveIcon from '@mui/icons-material/Save';
import SchoolIcon from '@mui/icons-material/School';
import WorkIcon from '@mui/icons-material/Work';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Swal from 'sweetalert2';
import { SwalConfig } from '../../utils/swalConfig';
import dayjs from 'dayjs';

// Import static data structure
import { employeesData } from '../../Data/Kontak/Employee';

const AddEmployee = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeStep, setActiveStep] = useState(0);
  const [tabValue, setTabValue] = useState(0); // 0 = Simple, 1 = Full
  const [candidateData, setCandidateData] = useState(null);
  const [loadingCandidate, setLoadingCandidate] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [employeeFormData, setEmployeeFormData] = useState({
    // Original API fields
    full_name: '',
    emp_id: '',
    sex: '',
    birth_date: '',
    email: '',
    no_telp: '',
    is_active: 'Active',
    id_department: '',
    id_designation: '',
    id_job_level: '',
    id_bu: '',
    id_office: '',
    grade: '',
    education: '',
    education_major: '',
    collage: '',
    bpjs_tk: '',
    bpjs_kes: '',
    address: '',
    id_subdistrict: '',
    id_city: '',
    id_province: '',
    tax_category: '',
    pph21: '',
    photo: '',
    date_added: dayjs().format('YYYY-MM-DD'),
    date_modified: dayjs().format('YYYY-MM-DD'),
    
    // Additional static fields
    staticData: {
      personalData: {
        employee_id: '',
        employee_code: '',
        gender: '',
        employee_name: '',
        first_name: '',
        middle_name: '',
        last_name: '',
        birth_date: '',
        modified_by: '',
        active_status: 1,
        employment_status: '',
        remarks: '',
        seniority_date: '',
        birth_place: '',
        mother_maiden_name: '',
        marital_status: '',
        marital_date: '',
        religion: '',
        ethnicity: '',
        age: '',
        height_cm: '',
        weight_kg: '',
        phone: '',
        id_number: '',
        id_region: '',
        living_status: '',
        vehicle_type: '',
        vehicle_ownership: '',
        best_achievement: '',
        best_achievement_desc: '',
        worst_achievement: '',
        worst_achievement_desc: '',
        language_active: '',
        language_passive: '',
        education_sponsor: '',
        scholarship: '',
        scholarship_duration: '',
        scholarship_desc: '',
        influence_source: '',
        influence_note: '',
        publication: '',
        highschool_avg_score: '',
        highschool_subject_count: '',
        gpa: '',
        gpa_max: '',
        // SIM Data
        has_sim: false,
        sim_c_number: '',
        sim_c_valid_until: '',
        sim_a_number: '',
        sim_a_valid_until: '',
        sim_b_number: '',
        sim_b_valid_until: ''
      },
      history: {
        last_job_reason_leave: '',
        last_job_salary: '',
        last_job_benefits: '',
        job_most_liked: '',
        job_least_liked: '',
        hobby: '',
        sport: '',
        sport_desc: '',
        free_time: '',
        reading_hours: '',
        reading_topics: '',
        newspaper: '',
        magazine: ''
      },
      workHistory: [],
      socialActivities: [],
      family: [],
      applicationSkills: {
        apply_reason: '',
        apply_initiator: '',
        apply_initiator_note: '',
        desired_position: '',
        expected_salary: '',
        expected_benefits: '',
        skills: '',
        willing_medical: 0,
        willing_psychology: 0,
        willing_relocate: 0,
        willing_trial: 0,
        willing_transfer: 0,
        willing_rules: 0,
        computer_skill: 0,
        computer_programs: '',
        typing_level: '',
        driving_ability: '',
        vehicle_knowledge: 0
      },
      education: [],
      courses: [],
      generalHealth: {
        willing_original_certificate: 0,
        allow_contact_employer: 0,
        ever_fired: 0,
        decision_advice_source: '',
        decision_advice_note: '',
        personal_advice_source: '',
        personal_advice_note: '',
        reaction_to_criticism: '',
        common_illness: '',
        serious_illness: 0,
        serious_illness_desc: '',
        eye_disease: 0,
        lung_disease: 0,
        ear_disease: 0,
        diabetes: 0,
        hypertension: 0,
        heart_disease: 0,
        last_checkup: '',
        strength: '',
        weakness: ''
      },
      emergency_relations: [],
      former_relations: [],
      guarantors: [],
      workInfo: {
        business_unit: '',
        branch_id: '',
        branch_name: '',
        region: '',
        department: '',
        position: '',
        organization: '',
        composition: '',
        position_group: '',
        grade: '',
        employee_group: '',
        education: '',
        education_group: '',
        university: ''
      },
      address: {
        street: '',
        subdistrict: '',
        rt: '',
        rw: '',
        postal_code: '',
        city: '',
        county: '',
        province: '',
        country: '',
        location_id: ''
      },
      benefit: {
        lspsi: 0,
        lkoperasi: 0,
        lsbsi: 0,
        lspn: 0,
        lbpjs_ketenagakerjaan_date: '',
        lbpjs_ketenagakerjaan_no: '',
        laviva_register_date: '',
        laviva_number: '',
        lbpjs_kesehatan_date: '',
        lbpjs_kesehatan_no: '',
        lbumida_date: '',
        lbumida_no: '',
        lbpjs_pensiun_date: '',
        lbpjs_pensiun_no: '',
        company_insurance_no: ''
      },
      contact: {
        mobile_phone: '',
        alt_mobile_phone: '',
        previous_email: '',
        corporate_email: ''
      },
      tax: {
        tax_category: '',
        tax_id: '',
        payroll_seniority: '',
        salary_scale_date: '',
        year_tax_point_15: '',
        year_tax_point_20: ''
      },
      others: {
        office_type: '',
        pakta_integritas_2025: 0,
        rahasia_perusahaan_2025: 0,
        update_data_karyawan_2025: '',
        penjamin: '',
        update_penjamin_2025: '',
        scan_biodata: 0,
        screening_sdm: 0,
        vehicle_and_facility: 0,
        toll_fee: '',
        distance_km: ''
      }
    }
  });

  const [loading, setLoading] = useState(false);  // Add loading state
  const token = localStorage.getItem('token');

  const handleQuickAddDepartment = async () => {
    if (!newDepartmentCode || !newDepartmentName) return;
    try {
      const response = await fetch('https://apidashboard.eurekagroup.id/Department/add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          department_code: newDepartmentCode,
          department_name: newDepartmentName,
          is_active: true
        })
      });
      if (!response.ok) throw new Error('Gagal menambah departemen');
      const data = await response.json();
      setDepartments(prev => [...prev, data]);
      setEmployeeFormData(prev => ({ ...prev, id_department: data._id }));
      setNewDepartmentCode('');
      setNewDepartmentName('');
      setOpenAddDepartmentDialog(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handleQuickAddDesignation = async () => {
    if (!newDesignationCode || !newDesignationName) return;
    try {
      const response = await fetch('https://apidashboard.eurekagroup.id/designation/add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          designation_code: newDesignationCode,
          designation_name: newDesignationName,
          is_active: 1
        })
      });
      if (!response.ok) throw new Error('Gagal menambah posisi');
      const data = await response.json();
      setDesignations(prev => [...prev, data]);
      setEmployeeFormData(prev => ({ ...prev, id_designation: data._id }));
      setNewDesignationCode('');
      setNewDesignationName('');
      setOpenAddDesignationDialog(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handleQuickAddJoblevel = async () => {
    if (!newJoblevelCode || !newJoblevelName || !employeeFormData.id_bu) return;
    try {
      const response = await fetch('https://apidashboard.eurekagroup.id/joblevel/add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          joblevel_code: newJoblevelCode,
          joblevel_name: newJoblevelName,
          id_bu: employeeFormData.id_bu,
          is_active: 1
        })
      });
      if (!response.ok) throw new Error('Gagal menambah level pekerjaan');
      const data = await response.json();
      setJobLevels(prev => [...prev, data]);
      setEmployeeFormData(prev => ({ ...prev, id_job_level: data._id }));
      setNewJoblevelCode('');
      setNewJoblevelName('');
      setOpenAddJoblevelDialog(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handleQuickAddBu = async () => {
    if (!newBuCode || !newBuName) return;
    try {
      const response = await fetch('https://apidashboard.eurekagroup.id/bu/add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          bu_code: newBuCode,
          bu_name: newBuName,
          is_active: 1
        })
      });
      if (!response.ok) throw new Error('Gagal menambah unit bisnis');
      const data = await response.json();
      setBusinessUnits(prev => [...prev, data]);
      setEmployeeFormData(prev => ({ ...prev, id_bu: data._id }));
      setNewBuCode('');
      setNewBuName('');
      setOpenAddBuDialog(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handleQuickAddOffice = async () => {
    if (!newOfficeCode || !newOfficeName) return;
    try {
      const response = await fetch('https://apidashboard.eurekagroup.id/office/add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          office_code: newOfficeCode,
          office_name: newOfficeName,
          is_active: 1
        })
      });
      if (!response.ok) throw new Error('Gagal menambah kantor');
      const data = await response.json();
      setOffices(prev => [...prev, data]);
      setEmployeeFormData(prev => ({ ...prev, id_office: data._id }));
      setNewOfficeCode('');
      setNewOfficeName('');
      setOpenAddOfficeDialog(false);
    } catch (error) {
      console.error(error);
    }
  };
  const [message, setMessage] = useState('');
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [jobLevels, setJobLevels] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [offices, setOffices] = useState([]);
  const [masterDataLoading, setMasterDataLoading] = useState(true);
  const [openAddDepartmentDialog, setOpenAddDepartmentDialog] = useState(false);
  const [openAddDesignationDialog, setOpenAddDesignationDialog] = useState(false);
  const [openAddJoblevelDialog, setOpenAddJoblevelDialog] = useState(false);
  const [openAddBuDialog, setOpenAddBuDialog] = useState(false);
  const [openAddOfficeDialog, setOpenAddOfficeDialog] = useState(false);
  const [newDepartmentCode, setNewDepartmentCode] = useState('');
  const [newDepartmentName, setNewDepartmentName] = useState('');
  const [newDesignationCode, setNewDesignationCode] = useState('');
  const [newDesignationName, setNewDesignationName] = useState('');
  const [newJoblevelCode, setNewJoblevelCode] = useState('');
  const [newJoblevelName, setNewJoblevelName] = useState('');
  const [newBuCode, setNewBuCode] = useState('');
  const [newBuName, setNewBuName] = useState('');
  const [newOfficeCode, setNewOfficeCode] = useState('');
  const [newOfficeName, setNewOfficeName] = useState('');

  const stepsSimple = [
    'ID Karyawan & Foto',
    'Nama Lengkap',
    'Informasi Personal',
    'Informasi Pekerjaan',
    'Benefit & Pajak'
  ];

  const stepsFull = [
    'Data Pribadi',
    'Informasi Pekerjaan', 
    'Pendidikan & Lokasi',
    'Informasi Tambahan',
    'Data Kandidat (Riwayat)',
    'Data Kandidat (Keluarga)',
    'Data Kandidat (Kemampuan)',
    'Data Kandidat (Kesehatan)',
    'Pendidikan Formal',
    'Riwayat Pekerjaan',
    'Kursus & Pelatihan',
    'Relasi & Penjamin'
  ];

  const steps = tabValue === 0 ? stepsSimple : stepsFull;

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setActiveStep(0); // Reset to first step when changing tabs
  };

  // Auto-save candidate data to form state for simple mode
  useEffect(() => {
    if (tabValue === 0 && (candidateData || location.state?.candidateData)) {
    const candidate = candidateData || location.state?.candidateData;
      const fullName = candidate?.personalData?.full_name || '';
      const nameParts = fullName.split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts[nameParts.length - 1] || '';
      const middleName = nameParts.slice(1, -1).join(' ') || '';

      setEmployeeFormData(prev => ({
        ...prev,
        full_name: fullName,
        emp_id: prev.emp_id,
        sex: candidate.personalData?.gender || '-',
        birth_date: candidate.personalData?.birth_date || '-',
        address: candidate.personalData?.address || '-',
        no_telp: candidate.personalData?.phone || '-',
        email: prev.email || `${fullName.toLowerCase().replace(/\s+/g, '.')}@eurekagroup.id`,
        is_active: 'Active',
        id_department: prev.id_department,
        id_designation: prev.id_designation,
        id_job_level: prev.id_job_level,
        id_bu: prev.id_bu,
        id_office: prev.id_office,
        grade: prev.grade,
        bpjs_tk: prev.bpjs_tk,
        bpjs_kes: prev.bpjs_kes,
        tax_category: prev.tax_category,
        pph21: prev.pph21,
        id_subdistrict: prev.id_subdistrict,
        id_city: prev.id_city,
        id_province: prev.id_province,
        date_added: dayjs().format('YYYY-MM-DD'),
        date_modified: dayjs().format('YYYY-MM-DD'),
        birth_place: candidate.personalData?.birth_place || '-',
        religion: candidate.personalData?.religion || '-',
        ethnicity: candidate.personalData?.ethnicity || '-',
        age: candidate.personalData?.age || '-',
        height_cm: candidate.personalData?.height_cm || '-',
        weight_kg: candidate.personalData?.weight_kg || '-',
        id_number: candidate.personalData?.id_number || '-',
        id_region: candidate.personalData?.id_region || '-',
        living_status: candidate.personalData?.living_status || '-',
        vehicle_type: candidate.personalData?.vehicle_type || '-',
        vehicle_ownership: candidate.personalData?.vehicle_ownership || '-',
        best_achievement: candidate.personalData?.best_achievement || '-',
        best_achievement_desc: candidate.personalData?.best_achievement_desc || '-',
        worst_achievement: candidate.personalData?.worst_achievement || '-',
        worst_achievement_desc: candidate.personalData?.worst_achievement_desc || '-',
        language_active: candidate.personalData?.language_active || '-',
        language_passive: candidate.personalData?.language_passive || '-',
        education_sponsor: candidate.personalData?.education_sponsor || '-',
        scholarship: candidate.personalData?.scholarship || '-',
        scholarship_duration: candidate.personalData?.scholarship_duration || '-',
        scholarship_desc: candidate.personalData?.scholarship_desc || '-',
        influence_source: candidate.personalData?.influence_source || '-',
        influence_note: candidate.personalData?.influence_note || '-',
        publication: candidate.personalData?.publication || '-',
        highschool_avg_score: candidate.personalData?.highschool_avg_score || '-',
        highschool_subject_count: candidate.personalData?.highschool_subject_count || '-',
        gpa: candidate.personalData?.gpa || '-',
        gpa_max: candidate.personalData?.gpa_max || '-',
        marital_status: candidate.family?.marital_status || '-',
        marital_date: candidate.family?.marriage_date || '-',
        // History
        last_job_reason_leave: candidate.history?.last_job_reason_leave || '',
        last_job_salary: candidate.history?.last_job_salary || '',
        last_job_benefits: candidate.history?.last_job_benefits || '',
        job_most_liked: candidate.history?.job_most_liked || '',
        job_least_liked: candidate.history?.job_least_liked || '',
        hobby: candidate.history?.hobby || '',
        sport: candidate.history?.sport || '',
        sport_desc: candidate.history?.sport_desc || '',
        free_time: candidate.history?.free_time || '',
        reading_hours: candidate.history?.reading_hours || '',
        reading_topics: candidate.history?.reading_topics || '',
        newspaper: candidate.history?.newspaper || '',
        magazine: candidate.history?.magazine || '',
        // Application Skills
        apply_reason: candidate.applicationSkills?.apply_reason || '',
        apply_initiator: candidate.applicationSkills?.apply_initiator || '',
        apply_initiator_note: candidate.applicationSkills?.apply_initiator_note || '',
        desired_position: candidate.applicationSkills?.desired_position || '',
        expected_salary: candidate.applicationSkills?.expected_salary || '',
        expected_benefits: candidate.applicationSkills?.expected_benefits || '',
        skills: candidate.applicationSkills?.skills || '',
        willing_medical: candidate.applicationSkills?.willing_medical || 0,
        willing_psychology: candidate.applicationSkills?.willing_psychology || 0,
        willing_relocate: candidate.applicationSkills?.willing_relocate || 0,
        willing_trial: candidate.applicationSkills?.willing_trial || 0,
        willing_transfer: candidate.applicationSkills?.willing_transfer || 0,
        willing_rules: candidate.applicationSkills?.willing_rules || 0,
        computer_skill: candidate.applicationSkills?.computer_skill || 0,
        computer_programs: candidate.applicationSkills?.computer_programs || '',
        typing_level: candidate.applicationSkills?.typing_level || '',
        driving_ability: candidate.applicationSkills?.driving_ability || '',
        vehicle_knowledge: candidate.applicationSkills?.vehicle_knowledge || 0,
        // General Health
        willing_original_certificate: candidate.generalHealth?.willing_original_certificate || 0,
        allow_contact_employer: candidate.generalHealth?.allow_contact_employer || 0,
        ever_fired: candidate.generalHealth?.ever_fired || 0,
        decision_advice_source: candidate.generalHealth?.decision_advice_source || '',
        decision_advice_note: candidate.generalHealth?.decision_advice_note || '',
        personal_advice_source: candidate.generalHealth?.personal_advice_source || '',
        personal_advice_note: candidate.generalHealth?.personal_advice_note || '',
        reaction_to_criticism: candidate.generalHealth?.reaction_to_criticism || '',
        common_illness: candidate.generalHealth?.common_illness || '',
        serious_illness: candidate.generalHealth?.serious_illness || 0,
        serious_illness_desc: candidate.generalHealth?.serious_illness_desc || '',
        eye_disease: candidate.generalHealth?.eye_disease || 0,
        lung_disease: candidate.generalHealth?.lung_disease || 0,
        ear_disease: candidate.generalHealth?.ear_disease || 0,
        diabetes: candidate.generalHealth?.diabetes || 0,
        hypertension: candidate.generalHealth?.hypertension || 0,
        heart_disease: candidate.generalHealth?.heart_disease || 0,
        last_checkup: candidate.generalHealth?.last_checkup || '',
        strength: candidate.generalHealth?.strength || '',
        weakness: candidate.generalHealth?.weakness || '',
        // Arrays
        family_data: candidate.family?.members?.map(m => ({
          type: m.jenis,
          name: m.nama,
          gender: m.gender,
          age: m.usia,
          education: m.pendidikan_terakhir,
          job: m.pekerjaan,
          position: m.jabatan,
          company: m.perusahaan,
          notes: m.keterangan
        })) || [],
        education_data: candidate.education?.map(e => ({
          level: e.jenjang,
          school_name: e.nama_sekolah,
          location: e.tempat,
          graduation_year: e.tahun_lulus,
          gpa: e.rata_rata_nilai,
          major: e.jurusan
        })) || [],
        courses_data: candidate.courses?.map(c => ({
          instructor_institution: c.instruktur_lembaga,
          location: c.tempat,
          duration: c.lama_pendidikan,
          sponsor: c.yang_membiayai,
          notes: c.keterangan
        })) || [],
        social_activities_data: candidate.socialActivities?.map(s => ({
          year: s.tahun,
          activity_type: s.jenis_aktivitas,
          position: s.jabatan,
          organization: s.organisasi
        })) || [],
        emergency_relations_data: candidate.others?.emergency_relations?.map(er => ({
          name: er.nama,
          age: er.usia,
          address: er.alamat,
          phone: er.no_telepon,
          relationship: er.hubungan,
          notes: er.keterangan
        })) || [],
        former_relations_data: candidate.others?.former_relations?.map(fr => ({
          name: fr.nama,
          age: fr.usia,
          address: fr.alamat,
          phone: fr.no_telepon,
          relationship: fr.hubungan,
          notes: fr.keterangan
        })) || [],
        guarantors_data: candidate.others?.guarantors?.map(g => ({
          name: g.nama,
          age: g.usia,
          address: g.alamat,
          phone: g.no_telepon,
          relationship: g.hubungan,
          notes: g.keterangan
        })) || [],
        // Name mapping for staticData
        staticData: {
          ...prev.staticData,
          personalData: {
            ...prev.staticData.personalData,
            first_name: firstName,
            middle_name: middleName,
            last_name: lastName,
            employee_name: fullName
          }
        }
      }));
    }
  }, [tabValue, candidateData, location.state?.candidateData]);

  // Handle photo file upload
  const handlePhotoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        Swal.fire({
          ...SwalConfig.error,
          title: 'Format File Tidak Valid',
          text: 'Hanya file JPG dan PNG yang diperbolehkan'
        });
        return;
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        Swal.fire({
          ...SwalConfig.error,
          title: 'Ukuran File Terlalu Besar',
          text: 'Maksimal ukuran file 5MB'
        });
        return;
      }

      setPhotoFile(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setMasterDataLoading(true);
        console.log('Loading master data...');
        const departmentResponse = await fetch('https://apidashboard.eurekagroup.id/department');
        const designationResponse = await fetch('https://apidashboard.eurekagroup.id/designation');
        const jobLevelResponse = await fetch('https://apidashboard.eurekagroup.id/joblevel');
        const businessUnitResponse = await fetch('https://apidashboard.eurekagroup.id/bu');
        const officeResponse = await fetch('https://apidashboard.eurekagroup.id/office');

        const departments = await departmentResponse.json();
        const designations = await designationResponse.json();
        const jobLevels = await jobLevelResponse.json();
        const businessUnits = await businessUnitResponse.json();
        const offices = await officeResponse.json();

        console.log('Master data loaded:', { departments, designations, jobLevels, businessUnits, offices });

        // Handle different response structures and map _id to id
        const deptData = (departments.data || departments).map(item => ({
          ...item,
          id: item._id
        }));
        const desigData = (designations.data || designations).map(item => ({
          ...item,
          id: item._id
        }));
        const jobLevelData = (jobLevels.data || jobLevels).map(item => ({
          ...item,
          id: item._id
        }));
        const buData = (businessUnits.data || businessUnits).map(item => ({
          ...item,
          id: item._id
        }));
        const officeData = (offices.data || offices).map(item => ({
          ...item,
          id: item._id
        }));

        setDepartments(deptData);
        setDesignations(desigData);
        setJobLevels(jobLevelData);
        setBusinessUnits(buData);
        setOffices(officeData);
        setMasterDataLoading(false);
      } catch (error) {
        console.error('Error loading master data:', error);
        // Set empty arrays as fallback
        setDepartments([]);
        setDesignations([]);
        setJobLevels([]);
        setBusinessUnits([]);
        setOffices([]);
        setMasterDataLoading(false);
      }
    };

    fetchData();
  }, []);

  // Function to fetch candidate data
  const fetchCandidateData = async (candidateId) => {
    try {
      setLoadingCandidate(true);
      const response = await fetch(`https://apidashboard.eurekagroup.id/candidates/${candidateId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      const candidateData = result.data || result;
      // Map _id to id for frontend compatibility
      const mappedCandidate = {
        ...candidateData,
        id: candidateData._id || candidateData.id
      };
      setCandidateData(mappedCandidate);
      console.log('Fetched candidate data:', mappedCandidate);
      return mappedCandidate;
    } catch (error) {
      console.error('Error fetching candidate:', error);
      Swal.fire({
        ...SwalConfig.error,
        title: 'Error!',
        text: `Gagal memuat data kandidat: ${error.message}`
      });
      return null;
    } finally {
      setLoadingCandidate(false);
    }
  };

  // useEffect to fetch candidate data if candidateId is provided
  useEffect(() => {
    if (location.state?.candidateId && !location.state?.candidateData) {
      fetchCandidateData(location.state.candidateId);
    }
  }, [location.state?.candidateId]);

  // Handle mode from Employee page (Simple/Full)
  useEffect(() => {
    if (location.state?.mode) {
      const mode = location.state.mode === 'simple' ? 0 : 1;
      setTabValue(mode);
    }
  }, [location.state?.mode]);

  // useEffect to handle fetched candidate data
  useEffect(() => {
    if (candidateData && !location.state?.candidateData) {
      // Pre-fill form with fetched candidate data
      setEmployeeFormData(prevData => ({
        ...prevData,
        full_name: candidateData.personalData?.full_name || '',
        sex: candidateData.personalData?.gender || '',
        email: `${candidateData.personalData?.full_name?.toLowerCase().replace(/\s+/g, '.')}@eurekagroup.id` || '',
        no_telp: candidateData.personalData?.phone || '',
        address: candidateData.personalData?.address || '',
        staticData: {
          ...prevData.staticData,
          personalData: {
            ...prevData.staticData.personalData,
            employee_name: candidateData.personalData?.full_name || '',
            gender: candidateData.personalData?.gender || '',
            birth_date: candidateData.personalData?.birth_date || '',
            birth_place: candidateData.personalData?.birth_place || '',
            religion: candidateData.personalData?.religion || '',
            ethnicity: candidateData.personalData?.ethnicity || '',
            age: candidateData.personalData?.age || '',
            height_cm: candidateData.personalData?.height_cm || '',
            weight_kg: candidateData.personalData?.weight_kg || '',
            phone: candidateData.personalData?.phone || '',
            id_number: candidateData.personalData?.id_number || '',
            id_region: candidateData.personalData?.id_region || '',
            living_status: candidateData.personalData?.living_status || '',
            vehicle_type: candidateData.personalData?.vehicle_type || '',
            vehicle_ownership: candidateData.personalData?.vehicle_ownership || '',
            best_achievement: candidateData.personalData?.best_achievement || '',
            best_achievement_desc: candidateData.personalData?.best_achievement_desc || '',
            worst_achievement: candidateData.personalData?.worst_achievement || '',
            worst_achievement_desc: candidateData.personalData?.worst_achievement_desc || '',
            language_active: candidateData.personalData?.language_active || '',
            language_passive: candidateData.personalData?.language_passive || '',
            education_sponsor: candidateData.personalData?.education_sponsor || '',
            scholarship: candidateData.personalData?.scholarship || '',
            scholarship_duration: candidateData.personalData?.scholarship_duration || '',
            scholarship_desc: candidateData.personalData?.scholarship_desc || '',
            influence_source: candidateData.personalData?.influence_source || '',
            influence_note: candidateData.personalData?.influence_note || '',
            publication: candidateData.personalData?.publication || '',
            highschool_avg_score: candidateData.personalData?.highschool_avg_score || '',
            highschool_subject_count: candidateData.personalData?.highschool_subject_count || '',
            gpa: candidateData.personalData?.gpa || '',
            gpa_max: candidateData.personalData?.gpa_max || '',
            // SIM Data
            has_sim: candidateData.personalData?.has_sim || false,
            sim_c_number: candidateData.personalData?.sim_c_number || '',
            sim_c_valid_until: candidateData.personalData?.sim_c_valid_until || '',
            sim_a_number: candidateData.personalData?.sim_a_number || '',
            sim_a_valid_until: candidateData.personalData?.sim_a_valid_until || '',
            sim_b_number: candidateData.personalData?.sim_b_number || '',
            sim_b_valid_until: candidateData.personalData?.sim_b_valid_until || ''
          },
          history: {
            ...prevData.staticData.history,
            last_job_reason_leave: candidateData.history?.last_job_reason_leave || '',
            last_job_salary: candidateData.history?.last_job_salary || '',
            last_job_benefits: candidateData.history?.last_job_benefits || '',
            job_most_liked: candidateData.history?.job_most_liked || '',
            job_least_liked: candidateData.history?.job_least_liked || '',
            hobby: candidateData.history?.hobby || '',
            sport: candidateData.history?.sport || '',
            sport_desc: candidateData.history?.sport_desc || '',
            free_time: candidateData.history?.free_time || '',
            reading_hours: candidateData.history?.reading_hours || '',
            reading_topics: candidateData.history?.reading_topics || '',
            newspaper: candidateData.history?.newspaper || '',
            magazine: candidateData.history?.magazine || ''
          },
          workHistory: candidateData.workHistory || [],
          socialActivities: candidateData.socialActivities || [],
          family: candidateData.family?.members ? candidateData.family.members.map(member => ({
            type: member.jenis || '',
            name: member.nama || '',
            gender: member.gender || '',
            age: member.usia || '',
            education: member.pendidikan_terakhir || '',
            position: member.jabatan || '',
            job: member.pekerjaan || '',
            company: member.perusahaan || '',
            notes: member.keterangan || ''
          })) : [],
          education: candidateData.education ? candidateData.education.map(edu => ({
            level: edu.jenjang || '',
            school_name: edu.nama_sekolah || '',
            location: edu.tempat || '',
            graduation_year: edu.tahun_lulus || '',
            gpa: edu.rata_rata_nilai || '',
            major: edu.jurusan || ''
          })) : [],
          courses: candidateData.courses ? candidateData.courses.map(course => ({
            instructor_institution: course.instruktur_lembaga || '',
            location: course.tempat || '',
            duration: course.lama_pendidikan || '',
            funded_by: course.yang_membiayai || '',
            notes: course.keterangan || ''
          })) : [],
          applicationSkills: {
            ...prevData.staticData.applicationSkills,
            apply_reason: candidateData.applicationSkills?.apply_reason || '',
            desired_position: candidateData.applicationSkills?.desired_position || '',
            expected_salary: candidateData.applicationSkills?.expected_salary || '',
            expected_benefits: candidateData.applicationSkills?.expected_benefits || '',
            skills: candidateData.applicationSkills?.skills || '',
            computer_programs: candidateData.applicationSkills?.computer_programs || '',
            driving_ability: candidateData.applicationSkills?.driving_ability || '',
            vehicle_knowledge: candidateData.applicationSkills?.vehicle_knowledge || '',
            willing_medical: candidateData.applicationSkills?.willing_medical || '',
            willing_psychology: candidateData.applicationSkills?.willing_psychology || '',
            willing_relocate: candidateData.applicationSkills?.willing_relocate || '',
            willing_trial: candidateData.applicationSkills?.willing_trial || '',
            willing_transfer: candidateData.applicationSkills?.willing_transfer || '',
            willing_rules: candidateData.applicationSkills?.willing_rules || '',
            computer_skill: candidateData.applicationSkills?.computer_skill || '',
            typing_level: candidateData.applicationSkills?.typing_level || ''
          },
          generalHealth: candidateData.generalHealth || {},
          others: candidateData.others || {},
          emergency_relations: candidateData.others?.emergency_relations ? candidateData.others.emergency_relations.map(relation => ({
            name: relation.nama || '',
            age: relation.usia || '',
            address: relation.alamat || '',
            phone: relation.no_telepon || '',
            relationship: relation.hubungan || '',
            notes: relation.keterangan || ''
          })) : [],
          former_relations: candidateData.others?.former_relations ? candidateData.others.former_relations.map(relation => ({
            name: relation.nama || '',
            age: relation.usia || '',
            address: relation.alamat || '',
            phone: relation.no_telepon || '',
            relationship: relation.hubungan || '',
            notes: relation.keterangan || ''
          })) : [],
          guarantors: candidateData.others?.guarantors ? candidateData.others.guarantors.map(guarantor => ({
            name: guarantor.nama || '',
            age: guarantor.usia || '',
            address: guarantor.alamat || '',
            phone: guarantor.no_telepon || '',
            relationship: guarantor.hubungan || '',
            notes: guarantor.keterangan || ''
          })) : []
        }
      }));
    }
  }, [candidateData]);

  // useEffect to handle candidate data
  useEffect(() => {
    if (location.state?.candidateData && location.state?.fromCandidate) {
      const candidateData = location.state.candidateData;
      console.log('=== CANDIDATE DATA MAPPING DEBUG ===');
      console.log('Original candidate data:', candidateData);
      
      // Pre-fill form with candidate data - using same structure as print
      setEmployeeFormData(prevData => ({
        ...prevData,
        // Map candidate data to employee form fields - EXACTLY as candidate structure
        full_name: candidateData.full_name || '',
        sex: candidateData.gender || '',
        email: `${candidateData.full_name?.toLowerCase().replace(/\s+/g, '.')}@eurekagroup.id` || '',
        no_telp: candidateData.phone || '',
        address: candidateData.address || '',
        staticData: {
          ...prevData.staticData,
          personalData: {
            ...prevData.staticData.personalData,
            employee_name: candidateData.full_name || '',
            gender: candidateData.gender || '',
            birth_date: candidateData.birth_date || '',
            birth_place: candidateData.birth_place || '',
            religion: candidateData.religion || '',
            ethnicity: candidateData.ethnicity || '',
            age: candidateData.age || '',
            height_cm: candidateData.height_cm || '',
            weight_kg: candidateData.weight_kg || '',
            phone: candidateData.phone || '',
            id_number: candidateData.id_number || '',
            id_region: candidateData.id_region || '',
            living_status: candidateData.living_status || '',
            vehicle_type: candidateData.vehicle_type || '',
            vehicle_ownership: candidateData.vehicle_ownership || '',
            best_achievement: candidateData.best_achievement || '',
            best_achievement_desc: candidateData.best_achievement_desc || '',
            worst_achievement: candidateData.worst_achievement || '',
            worst_achievement_desc: candidateData.worst_achievement_desc || '',
            language_active: candidateData.language_active || '',
            language_passive: candidateData.language_passive || '',
            education_sponsor: candidateData.education_sponsor || '',
            scholarship: candidateData.scholarship || '',
            scholarship_duration: candidateData.scholarship_duration || '',
            scholarship_desc: candidateData.scholarship_desc || '',
            influence_source: candidateData.influence_source || '',
            influence_note: candidateData.influence_note || '',
            publication: candidateData.publication || '',
            highschool_avg_score: candidateData.highschool_avg_score || '',
            highschool_subject_count: candidateData.highschool_subject_count || '',
            gpa: candidateData.gpa || '',
            gpa_max: candidateData.gpa_max || '',
            // SIM Data
            has_sim: candidateData.has_sim || false,
            sim_c_number: candidateData.sim_c_number || '',
            sim_c_valid_until: candidateData.sim_c_valid_until || '',
            sim_a_number: candidateData.sim_a_number || '',
            sim_a_valid_until: candidateData.sim_a_valid_until || '',
            sim_b_number: candidateData.sim_b_number || '',
            sim_b_valid_until: candidateData.sim_b_valid_until || ''
          },
          history: {
            ...prevData.staticData.history,
            last_job_reason_leave: candidateData.history?.last_job_reason_leave || '',
            last_job_salary: candidateData.history?.last_job_salary || '',
            last_job_benefits: candidateData.history?.last_job_benefits || '',
            job_most_liked: candidateData.history?.job_most_liked || '',
            job_least_liked: candidateData.history?.job_least_liked || '',
            hobby: candidateData.history?.hobby || '',
            sport: candidateData.history?.sport || '',
            sport_desc: candidateData.history?.sport_desc || '',
            free_time: candidateData.history?.free_time || '',
            reading_hours: candidateData.history?.reading_hours || '',
            reading_topics: candidateData.history?.reading_topics || '',
            newspaper: candidateData.history?.newspaper || '',
            magazine: candidateData.history?.magazine || ''
          },
          workHistory: candidateData.workHistory || [],
          socialActivities: candidateData.socialActivities || [],
          family: candidateData.family?.members ? candidateData.family.members.map(member => ({
            type: member.jenis || '',
            name: member.nama || '',
            gender: member.gender || '',
            age: member.usia || '',
            education: member.pendidikan_terakhir || '',
            position: member.jabatan || '',
            job: member.pekerjaan || '',
            company: member.perusahaan || '',
            notes: member.keterangan || ''
          })) : [],
          education: candidateData.education ? candidateData.education.map(edu => ({
            level: edu.jenjang || '',
            school_name: edu.nama_sekolah || '',
            location: edu.tempat || '',
            graduation_year: edu.tahun_lulus || '',
            gpa: edu.rata_rata_nilai || '',
            major: edu.jurusan || ''
          })) : [],
          courses: candidateData.courses ? candidateData.courses.map(course => ({
            instructor_institution: course.instruktur_lembaga || '',
            location: course.tempat || '',
            duration: course.lama_pendidikan || '',
            funded_by: course.yang_membiayai || '',
            notes: course.keterangan || ''
          })) : [],
          applicationSkills: {
            ...prevData.staticData.applicationSkills,
            apply_reason: candidateData.history?.job_most_liked || '',
            desired_position: candidateData.position || '',
            expected_salary: candidateData.salary || '',
            expected_benefits: candidateData.benefits || '',
            skills: candidateData.skills || '',
            computer_programs: candidateData.computer_programs || '',
            driving_ability: candidateData.driving_ability || '',
            vehicle_knowledge: candidateData.vehicle_knowledge || '',
            willing_medical: candidateData.willing_medical || '',
            willing_psychology: candidateData.willing_psychology || '',
            willing_relocate: candidateData.willing_relocate || '',
            willing_trial: candidateData.willing_trial || '',
            willing_transfer: candidateData.willing_transfer || '',
            willing_rules: candidateData.willing_rules || '',
            computer_skill: candidateData.computer_skill || '',
            typing_level: candidateData.typing_level || ''
          },
          generalHealth: candidateData.generalHealth || {},
          others: candidateData.others || {},
          emergency_relations: candidateData.others?.emergency_relations ? candidateData.others.emergency_relations.map(relation => ({
            name: relation.nama || '',
            age: relation.usia || '',
            address: relation.alamat || '',
            phone: relation.no_telepon || '',
            relationship: relation.hubungan || '',
            notes: relation.keterangan || ''
          })) : [],
          former_relations: candidateData.others?.former_relations ? candidateData.others.former_relations.map(relation => ({
            name: relation.nama || '',
            age: relation.usia || '',
            address: relation.alamat || '',
            phone: relation.no_telepon || '',
            relationship: relation.hubungan || '',
            notes: relation.keterangan || ''
          })) : [],
          guarantors: candidateData.others?.guarantors ? candidateData.others.guarantors.map(guarantor => ({
            name: guarantor.nama || '',
            age: guarantor.usia || '',
            address: guarantor.alamat || '',
            phone: guarantor.no_telepon || '',
            relationship: guarantor.hubungan || '',
            notes: guarantor.keterangan || ''
          })) : []
        }
      }));
      
      // Log the mapped data for debugging
      console.log('Mapped education data:', candidateData.education ? candidateData.education.map(edu => ({
        level: edu.jenjang || '',
        school_name: edu.nama_sekolah || '',
        location: edu.tempat || '',
        graduation_year: edu.tahun_lulus || '',
        gpa: edu.rata_rata_nilai || '',
        major: edu.jurusan || ''
      })) : []);
      
      console.log('Mapped courses data:', candidateData.courses ? candidateData.courses.map(course => ({
        instructor_institution: course.instruktur_lembaga || '',
        location: course.tempat || '',
        duration: course.lama_pendidikan || '',
        funded_by: course.yang_membiayai || '',
        notes: course.keterangan || ''
      })) : []);
      
      console.log('Mapped family data:', candidateData.family?.members ? candidateData.family.members.map(member => ({
        type: member.jenis || '',
        name: member.nama || '',
        gender: member.gender || '',
        age: member.usia || '',
        education: member.pendidikan_terakhir || '',
        position: member.jabatan || '',
        job: member.pekerjaan || '',
        company: member.perusahaan || '',
        notes: member.keterangan || ''
      })) : []);
    }
  }, [location.state]);

  const handleCancel = () => {
    // Navigate back to the employee list page
    navigate('/kontak/employee');
  };

  const handleNext = () => {
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSaveEmployee = async () => {
  const apiUrl = 'https://apidashboard.eurekagroup.id/employee/add';
  const candidate = candidateData || location.state?.candidateData;
  
  // Validation for required fields based on mode
  const missingFields = [];
  const formData = new FormData();
    // Helper: always return a string, never array
    function fixValue(...args) {
      for (const val of args) {
        if (Array.isArray(val)) {
          // Ambil string pertama yang valid, jika tidak ada, return '-'
          const valid = val.find(v => typeof v === 'string' && v.trim() !== '');
          if (typeof valid === 'string' && valid.trim() !== '') return valid;
        } else if (typeof val === 'string' && val.trim() !== '') {
          return val;
        }
      }
      return '-';
    }
    if (tabValue === 0) {
      // Simple mode validation - check from form or candidate
  function ensureString(val) {
    if (Array.isArray(val)) {
      // Ambil string pertama yang valid, jika tidak ada, return '-'
      const valid = val.find(v => typeof v === 'string' && v.trim() !== '');
      return typeof valid === 'string' && valid.trim() !== '' ? valid : '-';
    }
    return typeof val === 'string' ? val : '-';
  }
  function toSingleString(val1, val2) {
    const pick = v => {
      if (Array.isArray(v)) {
        const valid = v.find(x => typeof x === 'string' && x.trim() !== '');
        return valid ? valid : '';
      }
      if (typeof v === 'string' && v.trim() !== '') return v;
      return '';
    };
    return pick(val1) || pick(val2) || '';
  }
  
      if (!(employeeFormData.full_name?.trim() || candidate?.personalData?.full_name?.trim())) missingFields.push('Nama Lengkap');
      if (!((employeeFormData.sex && employeeFormData.sex !== '') || candidate?.personalData?.gender)) missingFields.push('Jenis Kelamin');
      if (!employeeFormData.emp_id?.trim()) missingFields.push('ID Karyawan');
      // Photo is optional - removed validation
      if (!employeeFormData.staticData?.personalData?.first_name?.trim()) missingFields.push('Nama Depan');
      if (!employeeFormData.staticData?.personalData?.last_name?.trim()) missingFields.push('Nama Belakang');
      if (!employeeFormData.id_department) missingFields.push('Departemen');
      if (!employeeFormData.id_designation) missingFields.push('Posisi');
      if (!employeeFormData.id_job_level) missingFields.push('Level Pekerjaan');
      if (!employeeFormData.id_bu) missingFields.push('Unit Bisnis');
      if (!employeeFormData.id_office) missingFields.push('Kantor');
    } else {
      // Full mode validation
      if (!employeeFormData.full_name?.trim()) missingFields.push('Nama Lengkap');
      if (!employeeFormData.email?.trim()) missingFields.push('Email');
      if (!employeeFormData.no_telp?.trim()) missingFields.push('Nomor Telepon');
      if (!employeeFormData.sex || employeeFormData.sex === '') missingFields.push('Jenis Kelamin');
      if (!employeeFormData.birth_date) missingFields.push('Tanggal Lahir');
      // Photo is optional - removed validation
    }
  
    if (missingFields.length > 0) {
      SwalConfig.validationWarning(missingFields);
      return;
    }
  
  // Prepare the current date
  const currentDate = new Date().toISOString().split('T')[0]; // Format YYYY-MM-DD
  
    // === BASIC EMPLOYEE INFO (FORM FIELDS LANGSUNG) ===
    // For Simple mode, build a single deterministic payload: pick values from the form first,
    // then fallback to candidate data. Coerce arrays to a single string and append each key once.
    if (tabValue === 0 && (candidateData || location.state?.candidateData)) {
      const candidate = candidateData || location.state?.candidateData;

      // Helper to pick a single string value from possible sources (handles arrays)
      const pick = (...sources) => {
        const isMeaningful = (v) => {
          if (typeof v === 'string') {
            const t = v.trim();
            if (t === '' || t === '-' || t.toLowerCase() === 'n/a') return false;
            return true;
          }
          return typeof v === 'number' || typeof v === 'boolean';
        };

        for (const src of sources) {
          if (Array.isArray(src)) {
            const valid = src.find(v => typeof v === 'string' && v.trim() !== '' && v.trim() !== '-');
            if (valid) return valid;
          } else if (isMeaningful(src)) {
            return typeof src === 'number' || typeof src === 'boolean' ? String(src) : src;
          }
        }
        return '';
      };

      // Map top-level values (form first, then candidate)
      const appendOnce = (key, value) => {
        formData.append(key, value != null ? value : '');
      };

      appendOnce('full_name', pick(employeeFormData.full_name, candidate.personalData?.full_name));
      appendOnce('emp_id', pick(employeeFormData.emp_id));
      appendOnce('sex', pick(employeeFormData.sex, candidate.personalData?.gender));
      appendOnce('birth_date', pick(employeeFormData.birth_date, candidate.personalData?.birth_date));
      appendOnce('email', pick(employeeFormData.email, candidate.personalData?.email, (candidate.personalData?.full_name || '').toLowerCase().replace(/\s+/g, '.') + '@eurekagroup.id'));
      appendOnce('no_telp', pick(employeeFormData.no_telp, candidate.personalData?.phone));
      appendOnce('address', pick(employeeFormData.address, candidate.personalData?.address));
      appendOnce('id_department', pick(employeeFormData.id_department));
      appendOnce('id_designation', pick(employeeFormData.id_designation));
      appendOnce('id_job_level', pick(employeeFormData.id_job_level));
      appendOnce('id_bu', pick(employeeFormData.id_bu));
      appendOnce('id_office', pick(employeeFormData.id_office));
      appendOnce('grade', pick(employeeFormData.grade));
      appendOnce('is_active', pick(employeeFormData.is_active, 'Active'));
      appendOnce('date_added', pick(employeeFormData.date_added, currentDate));
      appendOnce('date_modified', pick(employeeFormData.date_modified, currentDate));
      appendOnce('bpjs_tk', pick(employeeFormData.bpjs_tk));
      appendOnce('bpjs_kes', pick(employeeFormData.bpjs_kes));
      appendOnce('tax_category', pick(employeeFormData.tax_category));
      appendOnce('pph21', pick(employeeFormData.pph21));
      appendOnce('id_subdistrict', pick(employeeFormData.id_subdistrict));
      appendOnce('id_city', pick(employeeFormData.id_city));
      appendOnce('id_province', pick(employeeFormData.id_province));

      // PersonalData flattened keys (bracket notation) - pick single values only
      const pd = candidate.personalData || {};
      const ePd = employeeFormData.staticData?.personalData || {};
      const addPd = (k, ...sources) => formData.append(`personalData[${k}]`, pick(...sources));

      addPd('employee_name', pick(employeeFormData.full_name, pd.full_name, pd.employee_name, ePd.employee_name));
      addPd('gender', pick(employeeFormData.sex, pd.gender, ePd.gender));
      addPd('birth_place', pick(pd.birth_place, ePd.birth_place, employeeFormData.birth_place));
      addPd('religion', pick(pd.religion, ePd.religion, employeeFormData.religion));
      addPd('ethnicity', pick(pd.ethnicity, ePd.ethnicity, employeeFormData.ethnicity));
      addPd('age', pick(pd.age, ePd.age, employeeFormData.age));
      addPd('height_cm', pick(pd.height_cm, ePd.height_cm, employeeFormData.height_cm));
      addPd('weight_kg', pick(pd.weight_kg, ePd.weight_kg, employeeFormData.weight_kg));
      addPd('phone', pick(employeeFormData.no_telp, pd.phone, ePd.phone));
      addPd('id_number', pick(pd.id_number, ePd.id_number, employeeFormData.id_number));
      addPd('id_region', pick(pd.id_region, ePd.id_region, employeeFormData.id_region));
      addPd('living_status', pick(pd.living_status, ePd.living_status, employeeFormData.living_status));
      addPd('vehicle_type', pick(pd.vehicle_type, ePd.vehicle_type, employeeFormData.vehicle_type));
      addPd('vehicle_ownership', pick(pd.vehicle_ownership, ePd.vehicle_ownership, employeeFormData.vehicle_ownership));
      addPd('best_achievement', pick(pd.best_achievement, ePd.best_achievement, employeeFormData.best_achievement));
      addPd('best_achievement_desc', pick(pd.best_achievement_desc, ePd.best_achievement_desc, employeeFormData.best_achievement_desc));
      addPd('worst_achievement', pick(pd.worst_achievement, ePd.worst_achievement, employeeFormData.worst_achievement));
      addPd('worst_achievement_desc', pick(pd.worst_achievement_desc, ePd.worst_achievement_desc, employeeFormData.worst_achievement_desc));
      addPd('language_active', pick(pd.language_active, ePd.language_active, employeeFormData.language_active));
      addPd('language_passive', pick(pd.language_passive, ePd.language_passive, employeeFormData.language_passive));
      addPd('education_sponsor', pick(pd.education_sponsor, ePd.education_sponsor, employeeFormData.education_sponsor));
      addPd('scholarship', pick(pd.scholarship, ePd.scholarship, employeeFormData.scholarship));
      addPd('scholarship_duration', pick(pd.scholarship_duration, ePd.scholarship_duration, employeeFormData.scholarship_duration));
      addPd('scholarship_desc', pick(pd.scholarship_desc, ePd.scholarship_desc, employeeFormData.scholarship_desc));
      addPd('influence_source', pick(pd.influence_source, ePd.influence_source, employeeFormData.influence_source));
      addPd('influence_note', pick(pd.influence_note, ePd.influence_note, employeeFormData.influence_note));
      addPd('publication', pick(pd.publication, ePd.publication, employeeFormData.publication));
      addPd('highschool_avg_score', pick(pd.highschool_avg_score, ePd.highschool_avg_score, employeeFormData.highschool_avg_score));
      addPd('highschool_subject_count', pick(pd.highschool_subject_count, ePd.highschool_subject_count, employeeFormData.highschool_subject_count));
      addPd('gpa', pick(pd.gpa, ePd.gpa, employeeFormData.gpa));
      addPd('gpa_max', pick(pd.gpa_max, ePd.gpa_max, employeeFormData.gpa_max));
      addPd('photo', pick(pd.photo, ePd.photo, employeeFormData.photo ? 'uploaded' : ''));
      addPd('has_sim', pick(pd.has_sim, ePd.has_sim, employeeFormData.staticData?.personalData?.has_sim || false));
      // Ensure SIM numbers are present as empty strings if not provided
      addPd('sim_c_number', pick(pd.sim_c_number, ePd.sim_c_number, employeeFormData.staticData?.personalData?.sim_c_number || ''));
      addPd('sim_c_valid_until', pick(pd.sim_c_valid_until, ePd.sim_c_valid_until, employeeFormData.staticData?.personalData?.sim_c_valid_until || ''));
      addPd('sim_a_number', pick(pd.sim_a_number, ePd.sim_a_number, employeeFormData.staticData?.personalData?.sim_a_number || ''));
      addPd('sim_a_valid_until', pick(pd.sim_a_valid_until, ePd.sim_a_valid_until, employeeFormData.staticData?.personalData?.sim_a_valid_until || ''));
      addPd('sim_b_number', pick(pd.sim_b_number, ePd.sim_b_number, employeeFormData.staticData?.personalData?.sim_b_number || ''));
      addPd('sim_b_valid_until', pick(pd.sim_b_valid_until, ePd.sim_b_valid_until, employeeFormData.staticData?.personalData?.sim_b_valid_until || ''));

      // === HISTORY (flattened as history[...]) ===
      const hist = candidate.history || {};
      const eh = employeeFormData.staticData?.history || {};
      const addHistory = (k, ...sources) => formData.append(`history[${k}]`, pick(...sources));
      addHistory('last_job_reason_leave', pick(eh.last_job_reason_leave, hist.last_job_reason_leave));
      addHistory('last_job_salary', pick(eh.last_job_salary, hist.last_job_salary));
      addHistory('last_job_benefits', pick(eh.last_job_benefits, hist.last_job_benefits));
      addHistory('job_most_liked', pick(eh.job_most_liked, hist.job_most_liked));
      addHistory('job_least_liked', pick(eh.job_least_liked, hist.job_least_liked));
      addHistory('hobby', pick(eh.hobby, hist.hobby));
      addHistory('sport', pick(eh.sport, hist.sport));
      addHistory('sport_desc', pick(eh.sport_desc, hist.sport_desc));
      addHistory('free_time', pick(eh.free_time, hist.free_time));
      addHistory('reading_hours', pick(eh.reading_hours, hist.reading_hours));
      addHistory('reading_topics', pick(eh.reading_topics, hist.reading_topics));
      addHistory('newspaper', pick(eh.newspaper, hist.newspaper));
      addHistory('magazine', pick(eh.magazine, hist.magazine));

      // === APPLICATION SKILLS (applicationSkills[...]) ===
      const ask = employeeFormData.staticData?.applicationSkills || {};
      const cask = candidate.applicationSkills || {};
      const addApp = (k, ...sources) => formData.append(`applicationSkills[${k}]`, pick(...sources));
      addApp('apply_reason', pick(ask.apply_reason, cask.apply_reason));
      addApp('apply_initiator', pick(ask.apply_initiator, cask.apply_initiator));
      addApp('apply_initiator_note', pick(ask.apply_initiator_note, cask.apply_initiator_note));
      addApp('desired_position', pick(ask.desired_position, cask.desired_position));
      addApp('expected_salary', pick(ask.expected_salary, cask.expected_salary));
      addApp('expected_benefits', pick(ask.expected_benefits, cask.expected_benefits));
      addApp('skills', pick(ask.skills, cask.skills));
      addApp('willing_medical', pick(ask.willing_medical, cask.willing_medical, 0));
      addApp('willing_psychology', pick(ask.willing_psychology, cask.willing_psychology, 0));
      addApp('willing_relocate', pick(ask.willing_relocate, cask.willing_relocate, 0));
      addApp('willing_trial', pick(ask.willing_trial, cask.willing_trial, 0));
      addApp('willing_transfer', pick(ask.willing_transfer, cask.willing_transfer, 0));
      addApp('willing_rules', pick(ask.willing_rules, cask.willing_rules, 0));
      addApp('computer_skill', pick(ask.computer_skill, cask.computer_skill, 0));
      addApp('computer_programs', pick(ask.computer_programs, cask.computer_programs));
      addApp('typing_level', pick(ask.typing_level, cask.typing_level));
      addApp('driving_ability', pick(ask.driving_ability, cask.driving_ability));
      addApp('vehicle_knowledge', pick(ask.vehicle_knowledge, cask.vehicle_knowledge, 0));

      // === GENERAL HEALTH (generalHealth[...]) ===
      const gh = employeeFormData.staticData?.generalHealth || {};
      const cgh = candidate.generalHealth || {};
      const addGH = (k, ...sources) => formData.append(`generalHealth[${k}]`, pick(...sources));
      addGH('willing_original_certificate', pick(gh.willing_original_certificate, cgh.willing_original_certificate, 0));
      addGH('allow_contact_employer', pick(gh.allow_contact_employer, cgh.allow_contact_employer, 0));
      addGH('ever_fired', pick(gh.ever_fired, cgh.ever_fired, 0));
      addGH('decision_advice_source', pick(gh.decision_advice_source, cgh.decision_advice_source));
      addGH('decision_advice_note', pick(gh.decision_advice_note, cgh.decision_advice_note));
      addGH('personal_advice_source', pick(gh.personal_advice_source, cgh.personal_advice_source));
      addGH('personal_advice_note', pick(gh.personal_advice_note, cgh.personal_advice_note));
      addGH('reaction_to_criticism', pick(gh.reaction_to_criticism, cgh.reaction_to_criticism));
      addGH('common_illness', pick(gh.common_illness, cgh.common_illness));
      addGH('serious_illness', pick(gh.serious_illness, cgh.serious_illness, 0));
      addGH('serious_illness_desc', pick(gh.serious_illness_desc, cgh.serious_illness_desc));
      addGH('eye_disease', pick(gh.eye_disease, cgh.eye_disease, 0));
      addGH('lung_disease', pick(gh.lung_disease, cgh.lung_disease, 0));
      addGH('ear_disease', pick(gh.ear_disease, cgh.ear_disease, 0));
      addGH('diabetes', pick(gh.diabetes, cgh.diabetes, 0));
      addGH('hypertension', pick(gh.hypertension, cgh.hypertension, 0));
      addGH('heart_disease', pick(gh.heart_disease, cgh.heart_disease, 0));
      addGH('last_checkup', pick(gh.last_checkup, cgh.last_checkup));
      addGH('strength', pick(gh.strength, cgh.strength));
      addGH('weakness', pick(gh.weakness, cgh.weakness));

  // Also append flat keys for history, applicationSkills and generalHealth so Simple-mode
  // payload contains the same flat fields as Full-mode (dynamic: form -> candidate).
  const appendFlat = (k, ...sources) => appendOnce(k, pick(...sources));

  // Flat history fields
  appendFlat('last_job_reason_leave', eh.last_job_reason_leave, hist.last_job_reason_leave);
  appendFlat('last_job_salary', eh.last_job_salary, hist.last_job_salary);
  appendFlat('hobby', eh.hobby, hist.hobby);
  appendFlat('sport', eh.sport, hist.sport);
  appendFlat('sport_desc', eh.sport_desc, hist.sport_desc);
  appendFlat('free_time', eh.free_time, hist.free_time);
  appendFlat('reading_hours', eh.reading_hours, hist.reading_hours);
  appendFlat('reading_topics', eh.reading_topics, hist.reading_topics);
  appendFlat('newspaper', eh.newspaper, hist.newspaper);
  appendFlat('magazine', eh.magazine, hist.magazine);

  // Flat application skills
  appendFlat('apply_reason', ask.apply_reason, cask.apply_reason);
  appendFlat('apply_initiator', ask.apply_initiator, cask.apply_initiator);
  appendFlat('apply_initiator_note', ask.apply_initiator_note, cask.apply_initiator_note);
  appendFlat('desired_position', ask.desired_position, cask.desired_position);
  appendFlat('expected_salary', ask.expected_salary, cask.expected_salary);
  appendFlat('expected_benefits', ask.expected_benefits, cask.expected_benefits);
  appendFlat('skills', ask.skills, cask.skills);
  appendFlat('computer_programs', ask.computer_programs, cask.computer_programs);
  appendFlat('driving_ability', ask.driving_ability, cask.driving_ability);
  appendFlat('typing_level', ask.typing_level, cask.typing_level);
  appendFlat('computer_skill', ask.computer_skill, cask.computer_skill);
  appendFlat('vehicle_knowledge', ask.vehicle_knowledge, cask.vehicle_knowledge);

  // Flat general health fields
  appendFlat('reaction_to_criticism', gh.reaction_to_criticism, cgh.reaction_to_criticism);
  appendFlat('common_illness', gh.common_illness, cgh.common_illness);
  appendFlat('last_checkup', gh.last_checkup, cgh.last_checkup);
  appendFlat('strength', gh.strength, cgh.strength);
  appendFlat('weakness', gh.weakness, cgh.weakness);

      // === MULTIPLE ARRAYS (include in Simple payload to match desired format) ===
      // Education - prefer form values, fallback to candidate
      if (employeeFormData.staticData?.education?.length > 0) {
        employeeFormData.staticData.education.forEach((edu, index) => {
          formData.append(`education_data[${index}][level]`, edu.level || '');
          formData.append(`education_data[${index}][school_name]`, edu.school_name || '');
          formData.append(`education_data[${index}][location]`, edu.location || '');
          formData.append(`education_data[${index}][graduation_year]`, edu.graduation_year || '');
          formData.append(`education_data[${index}][gpa]`, edu.gpa || '');
          formData.append(`education_data[${index}][major]`, edu.major || '');
        });
      } else if (candidate.education?.length > 0) {
        candidate.education.forEach((edu, index) => {
          formData.append(`education_data[${index}][level]`, edu.jenjang || '');
          formData.append(`education_data[${index}][school_name]`, edu.nama_sekolah || '');
          formData.append(`education_data[${index}][location]`, edu.tempat || '');
          formData.append(`education_data[${index}][graduation_year]`, edu.tahun_lulus || '');
          formData.append(`education_data[${index}][gpa]`, edu.rata_rata_nilai || '');
          formData.append(`education_data[${index}][major]`, edu.jurusan || '');
        });
      }

      // Work History - prefer form then candidate
        // Helper: check if array only contains empty objects
        const isEmptyWorkHistory = arr => Array.isArray(arr) && arr.length > 0 && arr.every(w => {
          return !w.entry_date && !w.exit_date && !w.position && !w.company && !w.address_phone && !w.notes;
        });
        if (employeeFormData.staticData?.workHistory?.length > 0 && !isEmptyWorkHistory(employeeFormData.staticData.workHistory)) {
          employeeFormData.staticData.workHistory.forEach((work, index) => {
            formData.append(`work_history_data[${index}][entry_date]`, work.entry_date || '');
            formData.append(`work_history_data[${index}][exit_date]`, work.exit_date || '');
            formData.append(`work_history_data[${index}][position]`, work.position || '');
            formData.append(`work_history_data[${index}][company]`, work.company || '');
            formData.append(`work_history_data[${index}][address_phone]`, work.address_phone || '');
            formData.append(`work_history_data[${index}][notes]`, work.notes || '');
          });
        } else if (candidate.workHistory?.length > 0) {
          candidate.workHistory.forEach((w, index) => {
            formData.append(`work_history_data[${index}][entry_date]`, w.masuk || w.entry_date || '');
            formData.append(`work_history_data[${index}][exit_date]`, w.keluar || w.exit_date || '');
            formData.append(`work_history_data[${index}][position]`, w.jabatan || w.position || '');
            formData.append(`work_history_data[${index}][company]`, w.perusahaan || w.company || '');
            formData.append(`work_history_data[${index}][address_phone]`, w.alamat_telp || w.address_phone || '');
            formData.append(`work_history_data[${index}][notes]`, w.keterangan || w.notes || '');
          });
        }

      // Courses - prefer form then candidate
      if (employeeFormData.staticData?.courses?.length > 0) {
        employeeFormData.staticData.courses.forEach((course, index) => {
          formData.append(`courses_data[${index}][instructor_institution]`, course.instructor_institution || '');
          formData.append(`courses_data[${index}][location]`, course.location || '');
          formData.append(`courses_data[${index}][duration]`, course.duration || '');
          formData.append(`courses_data[${index}][sponsor]`, course.sponsor || course.funded_by || '');
          formData.append(`courses_data[${index}][notes]`, course.notes || '');
        });
      } else if (candidate.courses?.length > 0) {
        candidate.courses.forEach((c, index) => {
          formData.append(`courses_data[${index}][instructor_institution]`, c.instruktur_lembaga || '');
          formData.append(`courses_data[${index}][location]`, c.tempat || '');
          formData.append(`courses_data[${index}][duration]`, c.lama_pendidikan || '');
          formData.append(`courses_data[${index}][sponsor]`, c.yang_membiayai || '');
          formData.append(`courses_data[${index}][notes]`, c.keterangan || '');
        });
      }

      // Social Activities - prefer form then candidate
      if (employeeFormData.staticData?.socialActivities?.length > 0) {
        employeeFormData.staticData.socialActivities.forEach((activity, index) => {
          formData.append(`social_activities_data[${index}][year]`, activity.year || '');
          formData.append(`social_activities_data[${index}][activity_type]`, activity.activity_type || '');
          formData.append(`social_activities_data[${index}][position]`, activity.position || '');
          formData.append(`social_activities_data[${index}][organization]`, activity.organization || '');
        });
      } else if (candidate.socialActivities?.length > 0) {
        candidate.socialActivities.forEach((s, index) => {
          formData.append(`social_activities_data[${index}][year]`, s.tahun || '');
          formData.append(`social_activities_data[${index}][activity_type]`, s.jenis_aktivitas || '');
          formData.append(`social_activities_data[${index}][position]`, s.jabatan || '');
          formData.append(`social_activities_data[${index}][organization]`, s.organisasi || '');
        });
      }

      // Emergency Relations - prefer form then candidate
      if (employeeFormData.staticData?.emergency_relations?.length > 0) {
        employeeFormData.staticData.emergency_relations.forEach((relation, index) => {
          formData.append(`emergency_relations_data[${index}][name]`, relation.name || '');
          formData.append(`emergency_relations_data[${index}][age]`, relation.age || '');
          formData.append(`emergency_relations_data[${index}][address]`, relation.address || '');
          formData.append(`emergency_relations_data[${index}][phone]`, relation.phone || '');
          formData.append(`emergency_relations_data[${index}][relationship]`, relation.relationship || '');
          formData.append(`emergency_relations_data[${index}][notes]`, relation.notes || '');
        });
      } else if (candidate.others?.emergency_relations?.length > 0) {
        candidate.others.emergency_relations.forEach((r, index) => {
          formData.append(`emergency_relations_data[${index}][name]`, r.nama || '');
          formData.append(`emergency_relations_data[${index}][age]`, r.usia || '');
          formData.append(`emergency_relations_data[${index}][address]`, r.alamat || '');
          formData.append(`emergency_relations_data[${index}][phone]`, r.no_telepon || '');
          formData.append(`emergency_relations_data[${index}][relationship]`, r.hubungan || '');
          formData.append(`emergency_relations_data[${index}][notes]`, r.keterangan || '');
        });
      }

      // Former Relations - prefer form then candidate
      if (employeeFormData.staticData?.former_relations?.length > 0) {
        employeeFormData.staticData.former_relations.forEach((relation, index) => {
          formData.append(`former_relations_data[${index}][name]`, relation.name || '');
          formData.append(`former_relations_data[${index}][age]`, relation.age || '');
          formData.append(`former_relations_data[${index}][address]`, relation.address || '');
          formData.append(`former_relations_data[${index}][phone]`, relation.phone || '');
          formData.append(`former_relations_data[${index}][relationship]`, relation.relationship || '');
          formData.append(`former_relations_data[${index}][notes]`, relation.notes || '');
        });
      } else if (candidate.others?.former_relations?.length > 0) {
        candidate.others.former_relations.forEach((r, index) => {
          formData.append(`former_relations_data[${index}][name]`, r.nama || '');
          formData.append(`former_relations_data[${index}][age]`, r.usia || '');
          formData.append(`former_relations_data[${index}][address]`, r.alamat || '');
          formData.append(`former_relations_data[${index}][phone]`, r.no_telepon || '');
          formData.append(`former_relations_data[${index}][relationship]`, r.hubungan || '');
          formData.append(`former_relations_data[${index}][notes]`, r.keterangan || '');
        });
      }

      // Guarantors - prefer form then candidate
      if (employeeFormData.staticData?.guarantors?.length > 0) {
        employeeFormData.staticData.guarantors.forEach((guarantor, index) => {
          formData.append(`guarantors_data[${index}][name]`, guarantor.name || '');
          formData.append(`guarantors_data[${index}][age]`, guarantor.age || '');
          formData.append(`guarantors_data[${index}][address]`, guarantor.address || '');
          formData.append(`guarantors_data[${index}][phone]`, guarantor.phone || '');
          formData.append(`guarantors_data[${index}][relationship]`, guarantor.relationship || '');
          formData.append(`guarantors_data[${index}][notes]`, guarantor.notes || '');
        });
      } else if (candidate.others?.guarantors?.length > 0) {
        candidate.others.guarantors.forEach((g, index) => {
          formData.append(`guarantors_data[${index}][name]`, g.nama || '');
          formData.append(`guarantors_data[${index}][age]`, g.usia || '');
          formData.append(`guarantors_data[${index}][address]`, g.alamat || '');
          formData.append(`guarantors_data[${index}][phone]`, g.no_telepon || '');
          formData.append(`guarantors_data[${index}][relationship]`, g.hubungan || '');
          formData.append(`guarantors_data[${index}][notes]`, g.keterangan || '');
        });
      }
      
      
      
      // === APPLICATION SKILLS DATA ===
      
      
      // Add photo file if uploaded
      if (photoFile) {
        formData.append('photo', photoFile);
      }
      // Also append flat personal fields (unbracketed) and family_data[] to match the
      // exact payload format provided by the user (Simple mode should include both
      // personalData[...] and flat fields). Use the same pick(...) helper so values
      // come from the form first then candidate.
      // Note: these are intentionally duplicated keys in the payload the backend
      // expects according to the example you provided.
      const flatPick = (v1, v2) => pick(v1, v2);
      formData.append('first_name', flatPick(employeeFormData.staticData?.personalData?.first_name, candidate.personalData?.first_name));
      formData.append('middle_name', flatPick(employeeFormData.staticData?.personalData?.middle_name, candidate.personalData?.middle_name));
      formData.append('last_name', flatPick(employeeFormData.staticData?.personalData?.last_name, candidate.personalData?.last_name));
      formData.append('birth_place', flatPick(employeeFormData.staticData?.personalData?.birth_place, candidate.personalData?.birth_place));
      formData.append('religion', flatPick(employeeFormData.staticData?.personalData?.religion, candidate.personalData?.religion));
      formData.append('ethnicity', flatPick(employeeFormData.staticData?.personalData?.ethnicity, candidate.personalData?.ethnicity));
      formData.append('age', flatPick(employeeFormData.staticData?.personalData?.age, candidate.personalData?.age));
      formData.append('height_cm', flatPick(employeeFormData.staticData?.personalData?.height_cm, candidate.personalData?.height_cm));
      formData.append('weight_kg', flatPick(employeeFormData.staticData?.personalData?.weight_kg, candidate.personalData?.weight_kg));
      formData.append('marital_status', flatPick(employeeFormData.staticData?.personalData?.marital_status, candidate.family?.marital_status));
      // marital_date: only include candidate/form value if not Single
      const flatMarital = flatPick(employeeFormData.staticData?.personalData?.marital_date, candidate.family?.marriage_date);
      formData.append('marital_date', (formData.get('marital_status') && formData.get('marital_status').toLowerCase() !== 'single') ? flatMarital : '');
      formData.append('mother_maiden_name', flatPick(employeeFormData.staticData?.personalData?.mother_maiden_name, candidate.personalData?.mother_maiden_name));
      formData.append('living_status', flatPick(employeeFormData.staticData?.personalData?.living_status, candidate.personalData?.living_status));

      // Also append a `family_data[...]` block (some backends expect this name)
      if (employeeFormData.staticData?.family?.length > 0) {
        employeeFormData.staticData.family.forEach((member, index) => {
          formData.append(`family_data[${index}][type]`, member.type || '');
          formData.append(`family_data[${index}][name]`, member.name || '');
          formData.append(`family_data[${index}][gender]`, member.gender || '');
          formData.append(`family_data[${index}][age]`, member.age || '');
          formData.append(`family_data[${index}][education]`, member.education || '');
          formData.append(`family_data[${index}][position]`, member.position || '');
          formData.append(`family_data[${index}][job]`, member.job || '');
          formData.append(`family_data[${index}][company]`, member.company || '');
          formData.append(`family_data[${index}][notes]`, member.notes || '');
        });
      } else if (candidate.family?.members && candidate.family.members.length > 0) {
        candidate.family.members.forEach((m, index) => {
          formData.append(`family_data[${index}][type]`, m.jenis || '');
          formData.append(`family_data[${index}][name]`, m.nama || '');
          formData.append(`family_data[${index}][gender]`, m.gender || '');
          formData.append(`family_data[${index}][age]`, m.usia || '');
          formData.append(`family_data[${index}][education]`, m.pendidikan_terakhir || '');
          formData.append(`family_data[${index}][position]`, m.jabatan || '');
          formData.append(`family_data[${index}][job]`, m.pekerjaan || '');
          formData.append(`family_data[${index}][company]`, m.perusahaan || '');
          formData.append(`family_data[${index}][notes]`, m.keterangan || '');
        });
      }
    } else {
      // Full mode - auto-mapping from candidate if field empty
      const candidate = candidateData || location.state?.candidateData;
      if (candidate) {
        // Personal Data
        const pd = employeeFormData.staticData.personalData;
        const cpd = candidate.personalData || {};
        Object.keys(pd).forEach(key => {
          if ((pd[key] === '' || pd[key] === null || pd[key] === undefined) && cpd[key] !== undefined) pd[key] = cpd[key];
        });
        // Family
        const mapFamilyType = (jenis) => {
          if (['Ayah', 'Ibu'].includes(jenis)) return 'Orang Tua';
          if (['Suami', 'Istri'].includes(jenis)) return 'Suami/Istri';
          if (['Kakak', 'Adik'].includes(jenis)) return 'Saudara';
          if (jenis === 'Anak') return 'Anak';
          return jenis || '';
        };
        if (candidate.family?.members?.length > 0) {
          employeeFormData.staticData.family = candidate.family.members.map(m => ({
            type: mapFamilyType(m.jenis), // Jenis Keluarga
            name: m.nama || '', // Nama
            gender: m.gender || '', // L/P
            age: m.usia || '', // Usia
            education: m.pendidikan_terakhir || '', // Pendidikan
            job: m.pekerjaan || '', // Pekerjaan
            position: m.jabatan || '', // Jabatan
            company: m.perusahaan || '', // Perusahaan
            notes: m.keterangan || '' // Keterangan
          }));
        }
        // Application Skills
        const ask = employeeFormData.staticData.applicationSkills;
        const cak = candidate.applicationSkills || {};
        Object.keys(ask).forEach(key => {
          if ((ask[key] === '' || ask[key] === null || ask[key] === undefined) && cak[key] !== undefined) ask[key] = cak[key];
        });
        // General Health
        const gh = employeeFormData.staticData.generalHealth;
        const cgh = candidate.generalHealth || {};
        Object.keys(gh).forEach(key => {
          if ((gh[key] === '' || gh[key] === null || gh[key] === undefined) && cgh[key] !== undefined) gh[key] = cgh[key];
        });
        // History
        const hist = employeeFormData.staticData.history;
        const chist = candidate.history || {};
        Object.keys(hist).forEach(key => {
          if ((hist[key] === '' || hist[key] === null || hist[key] === undefined) && chist[key] !== undefined) hist[key] = chist[key];
        });
        // Education
        if (candidate.education?.length > 0) {
          employeeFormData.staticData.education = candidate.education.map(e => ({
            level: e.jenjang || '',
            school_name: e.nama_sekolah || '',
            location: e.tempat || '',
            graduation_year: e.tahun_lulus || '',
            gpa: e.rata_rata_nilai || '',
            major: e.jurusan || ''
          }));
        }
        // Courses
        if (candidate.courses?.length > 0) {
          employeeFormData.staticData.courses = candidate.courses.map(c => ({
            instructor_institution: c.instruktur_lembaga || '',
            location: c.tempat || '',
            duration: c.lama_pendidikan || '',
            funded_by: c.yang_membiayai || '',
            notes: c.keterangan || ''
          }));
        }
        // Social Activities
        if (candidate.socialActivities?.length > 0) {
          employeeFormData.staticData.socialActivities = candidate.socialActivities.map(a => ({
            year: a.tahun || '',
            activity_type: a.jenis_aktivitas || '',
            position: a.jabatan || '',
            organization: a.organisasi || ''
          }));
        }
        // Others
        if (candidate.others) {
          // Emergency Relations
          if (candidate.others.emergency_relations?.length > 0) {
            employeeFormData.staticData.emergency_relations = candidate.others.emergency_relations.map(r => ({
              name: r.nama || '',
              age: r.usia || '',
              address: r.alamat || '',
              phone: r.no_telepon || '',
              relationship: r.hubungan || '',
              notes: r.keterangan || ''
            }));
          }
          // Former Relations
          if (candidate.others.former_relations?.length > 0) {
            employeeFormData.staticData.former_relations = candidate.others.former_relations.map(r => ({
              name: r.nama || '',
              age: r.usia || '',
              address: r.alamat || '',
              phone: r.no_telepon || '',
              relationship: r.hubungan || '',
              notes: r.keterangan || ''
            }));
          }
          // Guarantors
          if (candidate.others.guarantors?.length > 0) {
            employeeFormData.staticData.guarantors = candidate.others.guarantors.map(g => ({
              name: g.nama || '',
              age: g.usia || '',
              address: g.alamat || '',
              phone: g.no_telepon || '',
              relationship: g.hubungan || '',
              notes: g.keterangan || ''
            }));
          }
        }
      }
      // ...existing code...
      formData.append('full_name', employeeFormData.full_name || '');
      formData.append('emp_id', employeeFormData.emp_id || '');
      formData.append('sex', employeeFormData.sex && (employeeFormData.sex === 'Male' || employeeFormData.sex === 'Female') ? employeeFormData.sex : '');
      formData.append('email', employeeFormData.email || '');
      formData.append('mobile_phone', employeeFormData.no_telp || '');
      formData.append('is_active', employeeFormData.is_active || 'Active');
      formData.append('id_department', employeeFormData.id_department || '');
      formData.append('id_designation', employeeFormData.id_designation || '');
      formData.append('id_job_level', employeeFormData.id_job_level || '');
      formData.append('id_bu', employeeFormData.id_bu || '');
      formData.append('id_office', employeeFormData.id_office || '');
      formData.append('grade', employeeFormData.grade || '');
      formData.append('education', employeeFormData.education || '');
      formData.append('education_major', employeeFormData.education_major || '');
      formData.append('collage', employeeFormData.collage || '');
      formData.append('bpjs_tk', employeeFormData.bpjs_tk || '');
      formData.append('bpjs_kes', employeeFormData.bpjs_kes || '');
      formData.append('address', employeeFormData.address || '');
      formData.append('id_subdistrict', employeeFormData.id_subdistrict || '');
      formData.append('id_city', employeeFormData.id_city || '');
      formData.append('id_province', employeeFormData.id_province || '');
      formData.append('tax_category', employeeFormData.tax_category || '');
      formData.append('pph21', employeeFormData.pph21 || '');
      // Only append birth_date once - use the main form data first, then fallback to staticData
      const birthDate = employeeFormData.birth_date || employeeFormData.staticData?.personalData?.birth_date || '';
      formData.append('birth_date', birthDate);
      formData.append('date_added', currentDate);
      formData.append('date_modified', currentDate);
      if (employeeFormData.photo) {
        formData.append('photo', employeeFormData.photo);
      }
    }
  
    // === CANDIDATE PERSONAL DATA (DARI FORM) ===
    // Field yang ADA DI FORM - renderPersonalDataForm (Step 0)
    // For Full mode, append personal name fields from the form (no candidate fallback needed here).
    if (tabValue !== 0) {
      formData.append('first_name', employeeFormData.staticData?.personalData?.first_name || '');
      formData.append('middle_name', employeeFormData.staticData?.personalData?.middle_name || '');
      formData.append('last_name', employeeFormData.staticData?.personalData?.last_name || '');
    }
    
  // Field yang ADA DI FORM - renderCandidateHistoryForm (Step 4)
  // birth_date already appended above, don't append again
    
    // Field yang ADA DI FORM - renderCandidateFamilyForm (Step 5)
    // Determine marital_status and marital_date carefully to avoid sending arrays or invalid dates.
    const candidateForFamily = candidateData || location.state?.candidateData;
    const getSingle = (v) => {
      if (Array.isArray(v)) {
        const valid = v.find(x => typeof x === 'string' && x.trim() !== '');
        return valid || '';
      }
      return typeof v === 'string' ? v : '';
    };
  const maritalStatusValue = getSingle(employeeFormData.staticData?.personalData?.marital_status) || getSingle(candidateForFamily?.family?.marital_status) || '';
  // Put marital info under family[...] as requested by backend format
  formData.append('family[marital_status]', maritalStatusValue);
  const maritalDateSource = getSingle(employeeFormData.staticData?.personalData?.marital_date) || getSingle(candidateForFamily?.family?.marriage_date) || '';
  const maritalDateValue = (maritalStatusValue && maritalStatusValue.toLowerCase() !== 'single') ? maritalDateSource : '';
  formData.append('family[marriage_date]', maritalDateValue);
  // marriage count (if any)
  const marriageCount = getSingle(employeeFormData.staticData?.personalData?.marriage_count) || getSingle(candidateForFamily?.family?.marriage_count) || '';
  formData.append('family[marriage_count]', marriageCount);
    // Append additional personal/history/skills/health fields from the form only in Full mode.
    // In Simple mode these fields were already normalized and appended above to avoid duplicates/arrays.
    if (tabValue !== 0) {
      formData.append('mother_maiden_name', employeeFormData.staticData?.personalData?.mother_maiden_name || '');
      formData.append('living_status', employeeFormData.staticData?.personalData?.living_status || '');

      // Field yang ADA DI FORM - renderCandidateHealthForm (Step 7)
      formData.append('best_achievement', employeeFormData.staticData?.personalData?.best_achievement || '');

      // === CANDIDATE HISTORY DATA (DARI FORM STEP 4 - renderCandidateHistoryForm) ===
      formData.append('last_job_reason_leave', employeeFormData.staticData?.history?.last_job_reason_leave || '');
      formData.append('last_job_salary', employeeFormData.staticData?.history?.last_job_salary || '');
      formData.append('hobby', employeeFormData.staticData?.history?.hobby || '');
      formData.append('sport', employeeFormData.staticData?.history?.sport || '');

      // === CANDIDATE SKILLS (DARI FORM STEP 6 - renderCandidateSkillsForm) ===
      formData.append('apply_reason', employeeFormData.staticData?.applicationSkills?.apply_reason || '');
      formData.append('desired_position', employeeFormData.staticData?.applicationSkills?.desired_position || '');
      formData.append('expected_salary', employeeFormData.staticData?.applicationSkills?.expected_salary || '');
      formData.append('skills', employeeFormData.staticData?.applicationSkills?.skills || '');
      formData.append('computer_programs', employeeFormData.staticData?.applicationSkills?.computer_programs || '');
      formData.append('driving_ability', employeeFormData.staticData?.applicationSkills?.driving_ability || '');

      // === CANDIDATE HEALTH (DARI FORM STEP 7 - renderCandidateHealthForm) ===
      formData.append('common_illness', employeeFormData.staticData?.generalHealth?.common_illness || '');
      formData.append('last_checkup', employeeFormData.staticData?.generalHealth?.last_checkup || '');
      formData.append('strength', employeeFormData.staticData?.generalHealth?.strength || '');
      formData.append('weakness', employeeFormData.staticData?.generalHealth?.weakness || '');
      formData.append('reaction_to_criticism', employeeFormData.staticData?.generalHealth?.reaction_to_criticism || '');
    }
  
  
    // === MULTIPLE DATA ARRAYS ===
    // Family Data - prefer form then candidate
    if (employeeFormData.staticData?.family?.length > 0) {
      employeeFormData.staticData.family.forEach((member, index) => {
        formData.append(`family_members_data[${index}][type]`, member.type || '');
        formData.append(`family_members_data[${index}][name]`, member.name || '');
        formData.append(`family_members_data[${index}][gender]`, member.gender || '');
        formData.append(`family_members_data[${index}][age]`, member.age || '');
        formData.append(`family_members_data[${index}][education]`, member.education || '');
        formData.append(`family_members_data[${index}][position]`, member.position || '');
        formData.append(`family_members_data[${index}][job]`, member.job || '');
        formData.append(`family_members_data[${index}][company]`, member.company || '');
        formData.append(`family_members_data[${index}][notes]`, member.notes || '');
      });
    } else if (candidate?.family?.members && candidate.family.members.length > 0) {
      candidate.family.members.forEach((m, index) => {
        formData.append(`family_members_data[${index}][type]`, m.jenis || '');
        formData.append(`family_members_data[${index}][name]`, m.nama || '');
        formData.append(`family_members_data[${index}][gender]`, m.gender || '');
        formData.append(`family_members_data[${index}][age]`, m.usia || '');
        formData.append(`family_members_data[${index}][education]`, m.pendidikan_terakhir || '');
        formData.append(`family_members_data[${index}][position]`, m.jabatan || '');
        formData.append(`family_members_data[${index}][job]`, m.pekerjaan || '');
        formData.append(`family_members_data[${index}][company]`, m.perusahaan || '');
        formData.append(`family_members_data[${index}][notes]`, m.keterangan || '');
      });
    }
  
    // Only add static data if NOT in simple mode (to prevent duplication)
    if (tabValue !== 0) {
      // Education Data
      if (employeeFormData.staticData?.education?.length > 0) {
        employeeFormData.staticData.education.forEach((edu, index) => {
          formData.append(`education_data[${index}][level]`, edu.level || '');
          formData.append(`education_data[${index}][school_name]`, edu.school_name || '');
          formData.append(`education_data[${index}][location]`, edu.location || '');
          formData.append(`education_data[${index}][graduation_year]`, edu.graduation_year || '');
          formData.append(`education_data[${index}][gpa]`, edu.gpa || '');
          formData.append(`education_data[${index}][major]`, edu.major || '');
        });
      }
    
      // Work History Data
      if (employeeFormData.staticData?.workHistory?.length > 0) {
        employeeFormData.staticData.workHistory.forEach((work, index) => {
          formData.append(`work_history_data[${index}][entry_date]`, work.entry_date || '');
          formData.append(`work_history_data[${index}][exit_date]`, work.exit_date || '');
          formData.append(`work_history_data[${index}][position]`, work.position || '');
          formData.append(`work_history_data[${index}][company]`, work.company || '');
          formData.append(`work_history_data[${index}][address_phone]`, work.address_phone || '');
          formData.append(`work_history_data[${index}][notes]`, work.notes || '');
        });
      }
    
      // Courses Data
      if (employeeFormData.staticData?.courses?.length > 0) {
        employeeFormData.staticData.courses.forEach((course, index) => {
          formData.append(`courses_data[${index}][instructor_institution]`, course.instructor_institution || '');
          formData.append(`courses_data[${index}][location]`, course.location || '');
          formData.append(`courses_data[${index}][duration]`, course.duration || '');
          formData.append(`courses_data[${index}][funded_by]`, course.funded_by || '');
          formData.append(`courses_data[${index}][notes]`, course.notes || '');
        });
      }
    
      // Social Activities Data
      if (employeeFormData.staticData?.socialActivities?.length > 0) {
        employeeFormData.staticData.socialActivities.forEach((activity, index) => {
          formData.append(`social_activities_data[${index}][year]`, activity.year || '');
          formData.append(`social_activities_data[${index}][activity_type]`, activity.activity_type || '');
          formData.append(`social_activities_data[${index}][position]`, activity.position || '');
          formData.append(`social_activities_data[${index}][organization]`, activity.organization || '');
          formData.append(`social_activities_data[${index}][notes]`, activity.notes || '');
        });
      }
    
      // Emergency Relations Data
      if (employeeFormData.staticData?.emergency_relations?.length > 0) {
        employeeFormData.staticData.emergency_relations.forEach((relation, index) => {
          formData.append(`emergency_relations_data[${index}][name]`, relation.name || '');
          formData.append(`emergency_relations_data[${index}][age]`, relation.age || '');
          formData.append(`emergency_relations_data[${index}][address]`, relation.address || '');
          formData.append(`emergency_relations_data[${index}][phone]`, relation.phone || '');
          formData.append(`emergency_relations_data[${index}][relationship]`, relation.relationship || '');
          formData.append(`emergency_relations_data[${index}][notes]`, relation.notes || '');
        });
      }
    
      // Former Relations Data
      if (employeeFormData.staticData?.former_relations?.length > 0) {
        employeeFormData.staticData.former_relations.forEach((relation, index) => {
          formData.append(`former_relations_data[${index}][name]`, relation.name || '');
          formData.append(`former_relations_data[${index}][age]`, relation.age || '');
          formData.append(`former_relations_data[${index}][address]`, relation.address || '');
          formData.append(`former_relations_data[${index}][phone]`, relation.phone || '');
          formData.append(`former_relations_data[${index}][relationship]`, relation.relationship || '');
          formData.append(`former_relations_data[${index}][notes]`, relation.notes || '');
        });
      }
    
      // Guarantors Data
      if (employeeFormData.staticData?.guarantors?.length > 0) {
        employeeFormData.staticData.guarantors.forEach((guarantor, index) => {
          formData.append(`guarantors_data[${index}][name]`, guarantor.name || '');
          formData.append(`guarantors_data[${index}][age]`, guarantor.age || '');
          formData.append(`guarantors_data[${index}][address]`, guarantor.address || '');
          formData.append(`guarantors_data[${index}][phone]`, guarantor.phone || '');
          formData.append(`guarantors_data[${index}][relationship]`, guarantor.relationship || '');
          formData.append(`guarantors_data[${index}][notes]`, guarantor.notes || '');
        });
      }
    }
  
    // === META INFO ===
    formData.append('data_source', 'frontend_form');
    formData.append('form_version', '1.0');
    formData.append('created_at', new Date().toISOString());
    formData.append('has_multiple_data[family]', (employeeFormData.staticData?.family?.length || 0) > 0 ? 'true' : 'false');
    formData.append('has_multiple_data[education]', (employeeFormData.staticData?.education?.length || 0) > 0 ? 'true' : 'false');
    formData.append('has_multiple_data[work_history]', (employeeFormData.staticData?.workHistory?.length || 0) > 0 ? 'true' : 'false');
    formData.append('has_multiple_data[courses]', (employeeFormData.staticData?.courses?.length || 0) > 0 ? 'true' : 'false');
    formData.append('has_multiple_data[social_activities]', (employeeFormData.staticData?.socialActivities?.length || 0) > 0 ? 'true' : 'false');
    formData.append('has_multiple_data[emergency_relations]', (employeeFormData.staticData?.emergency_relations?.length || 0) > 0 ? 'true' : 'false');
    formData.append('has_multiple_data[former_relations]', (employeeFormData.staticData?.former_relations?.length || 0) > 0 ? 'true' : 'false');
    formData.append('has_multiple_data[guarantors]', (employeeFormData.staticData?.guarantors?.length || 0) > 0 ? 'true' : 'false');
    formData.append('total_records[family_count]', employeeFormData.staticData?.family?.length || 0);
    formData.append('total_records[education_count]', employeeFormData.staticData?.education?.length || 0);
    formData.append('total_records[work_history_count]', employeeFormData.staticData?.workHistory?.length || 0);
    formData.append('total_records[courses_count]', employeeFormData.staticData?.courses?.length || 0);
    formData.append('total_records[social_activities_count]', employeeFormData.staticData?.socialActivities?.length || 0);
    formData.append('total_records[emergency_relations_count]', employeeFormData.staticData?.emergency_relations?.length || 0);
    formData.append('total_records[former_relations_count]', employeeFormData.staticData?.former_relations?.length || 0);
    formData.append('total_records[guarantors_count]', employeeFormData.staticData?.guarantors?.length || 0);
  
    // Debug logging
    console.log('=== FORM SUBMISSION DEBUG ===');
    console.log('Employee Form Data:', employeeFormData);
    console.log('Form data being sent:', Array.from(formData.entries()));
    console.log('API URL:', apiUrl);
    
    // Log specific sections for debugging
    console.log('Basic Info:', {
      full_name: employeeFormData.full_name,
      emp_id: employeeFormData.emp_id,
      email: employeeFormData.email,
      sex: employeeFormData.sex
    });
    
    console.log('Static Data:', employeeFormData.staticData);
    console.log('Family Data Count:', employeeFormData.staticData?.family?.length || 0);
    console.log('Education Data Count:', employeeFormData.staticData?.education?.length || 0);
    
    // Create a readable payload object for debugging
    const payloadObject = {};
    for (let [key, value] of formData.entries()) {
      payloadObject[key] = value;
    }
    console.log('Formatted Payload Object:', payloadObject);

    const token = localStorage.getItem('token'); // Get token from localStorage
  
    try {
      setLoading(true); // Set loading to true when submitting
      const response = await fetch(apiUrl, {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: `Bearer ${token}`, // Add Authorization header
        },
      });
  
      const result = await response.json();
      console.log('=== BACKEND RESPONSE ===');
      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      console.log('API response:', result);
      
      // Log if there are any validation errors
      if (result.errors) {
        console.log('Validation errors:', result.errors);
      }
      if (result.message) {
        console.log('Response message:', result.message);
      }
  
      // Adjusted success condition
      if (response.ok) {
        // Get the new employee ID from response
        const newEmployeeId = result.data?._id || result.data?.id || result._id;
        
        SwalConfig.success(
          'Berhasil Ditambahkan!',
          `Karyawan ${employeeFormData.full_name} berhasil ditambahkan!`,
          2500
        ).then(() => {
          // Redirect to ViewEmployee with new employee ID
          if (newEmployeeId) {
            navigate(`/kontak/employee/ViewEmployee/${newEmployeeId}`);
          } else {
            // Fallback to employee list if no ID returned
            navigate('/kontak/employee');
          }
        });
      } else {
        // Handle errors based on the response status
        SwalConfig.error(
          'Gagal Menambahkan!',
          `Gagal menambahkan karyawan: ${result.message || 'Kesalahan tidak diketahui'}`
        );
      }
    } catch (error) {
      console.error('Error saving employee data:', error);
      SwalConfig.error(
        'Error',
        'Error saving employee data: ' + error.message
      );
    } finally {
      setLoading(false); // Reset loading state after completion
    }
  };

  const renderPersonalDataForm = () => (
    <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 3 }}>
      <CardHeader
        avatar={
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <PersonIcon />
          </Avatar>
        }
        title="Data Pribadi"
        titleTypographyProps={{ fontWeight: 600 }}
      />
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nama Lengkap"
              value={employeeFormData.full_name}
              onChange={(e) => setEmployeeFormData({ ...employeeFormData, full_name: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="ID Karyawan"
              value={employeeFormData.emp_id}
              onChange={(e) => setEmployeeFormData({ ...employeeFormData, emp_id: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Nama Depan"
              value={employeeFormData.staticData.personalData.first_name}
              onChange={(e) => setEmployeeFormData({
                ...employeeFormData,
                staticData: {
                  ...employeeFormData.staticData,
                  personalData: {
                    ...employeeFormData.staticData.personalData,
                    first_name: e.target.value
                  }
                }
              })}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Nama Tengah"
              value={employeeFormData.staticData.personalData.middle_name}
              onChange={(e) => setEmployeeFormData({
                ...employeeFormData,
                staticData: {
                  ...employeeFormData.staticData,
                  personalData: {
                    ...employeeFormData.staticData.personalData,
                    middle_name: e.target.value
                  }
                }
              })}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Nama Belakang"
              value={employeeFormData.staticData.personalData.last_name}
              onChange={(e) => setEmployeeFormData({
                ...employeeFormData,
                staticData: {
                  ...employeeFormData.staticData,
                  personalData: {
                    ...employeeFormData.staticData.personalData,
                    last_name: e.target.value
                  }
                }
              })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>Jenis Kelamin</InputLabel>
              <Select
                value={employeeFormData.sex}
                onChange={(e) => setEmployeeFormData({ ...employeeFormData, sex: e.target.value })}
              >
                <MenuItem value="Male">Laki-laki</MenuItem>
                <MenuItem value="Female">Perempuan</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Status Aktif</InputLabel>
              <Select
                value={employeeFormData.is_active}
                onChange={(e) => setEmployeeFormData({ ...employeeFormData, is_active: e.target.value })}
              >
                <MenuItem value="Active">Aktif</MenuItem>
                <MenuItem value="Inactive">Tidak Aktif</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={employeeFormData.email}
              onChange={(e) => setEmployeeFormData({ ...employeeFormData, email: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nomor Telepon"
              value={employeeFormData.no_telp}
              onChange={(e) => setEmployeeFormData({ ...employeeFormData, no_telp: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Tanggal Lahir"
              type="date"
              value={employeeFormData.birth_date}
              onChange={(e) => setEmployeeFormData({ ...employeeFormData, birth_date: e.target.value })}
              InputLabelProps={{ 
                shrink: true,
                sx: { 
                  px: 0.5
                }
              }}
              inputProps={{
                placeholder: ''
              }}
              sx={{
                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                  marginLeft: 'auto'
                }
              }}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Alamat"
              multiline
              rows={3}
              value={employeeFormData.address}
              onChange={(e) => setEmployeeFormData({ ...employeeFormData, address: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Foto Karyawan
              </Typography>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setEmployeeFormData({ ...employeeFormData, photo: e.target.files[0] })}
                style={{
                  padding: '16px 8px 8px 8px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  width: '100%'
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderWorkInformationForm = () => (
    <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 3 }}>
      <CardHeader
        avatar={
          <Avatar sx={{ bgcolor: 'success.main' }}>
            <WorkIcon />
          </Avatar>
        }
        title="Informasi Pekerjaan"
        titleTypographyProps={{ fontWeight: 600 }}
      />
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Autocomplete
              fullWidth
              options={departments}
              getOptionLabel={(option) => option?.department_name || ''}
              value={departments.find(d => d._id === employeeFormData.id_department) || null}
              onChange={(_, newValue) => {
                if (newValue?.isAddNew) {
                  setOpenAddDepartmentDialog(true);
                } else {
                  setEmployeeFormData({
                    ...employeeFormData,
                    id_department: newValue?._id || ''
                  });
                }
              }}
              filterOptions={(options, params) => {
                const filtered = options.filter(option => 
                  option.department_name?.toLowerCase().includes(params.inputValue.toLowerCase())
                );
                if (params.inputValue !== '' && filtered.length === 0) {
                  filtered.push({
                    isAddNew: true,
                    department_name: `+ Tambah "${params.inputValue}"`,
                    _id: 'add-new'
                  });
                }
                return filtered;
              }}
              renderInput={(params) => (
                <TextField {...params} label="Departemen" placeholder="Cari departemen" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              fullWidth
              options={designations}
              getOptionLabel={(option) => option?.designation_name || ''}
              value={designations.find(d => d._id === employeeFormData.id_designation) || null}
              onChange={(_, newValue) => {
                if (newValue?.isAddNew) {
                  setOpenAddDesignationDialog(true);
                } else {
                  setEmployeeFormData({
                    ...employeeFormData,
                    id_designation: newValue?._id || ''
                  });
                }
              }}
              filterOptions={(options, params) => {
                const filtered = options.filter(option => 
                  option.designation_name?.toLowerCase().includes(params.inputValue.toLowerCase())
                );
                if (params.inputValue !== '' && filtered.length === 0) {
                  filtered.push({
                    isAddNew: true,
                    designation_name: `+ Tambah "${params.inputValue}"`,
                    _id: 'add-new'
                  });
                }
                return filtered;
              }}
              renderInput={(params) => (
                <TextField {...params} label="Posisi" placeholder="Cari posisi" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              fullWidth
              options={jobLevels}
              getOptionLabel={(option) => option?.joblevel_name || ''}
              value={jobLevels.find(j => j._id === employeeFormData.id_job_level) || null}
              onChange={(_, newValue) => {
                if (newValue?.isAddNew) {
                  setOpenAddJoblevelDialog(true);
                } else {
                  setEmployeeFormData({
                    ...employeeFormData,
                    id_job_level: newValue?._id || ''
                  });
                }
              }}
              filterOptions={(options, params) => {
                const filtered = options.filter(option => 
                  option.joblevel_name?.toLowerCase().includes(params.inputValue.toLowerCase())
                );
                if (params.inputValue !== '' && filtered.length === 0) {
                  filtered.push({
                    isAddNew: true,
                    joblevel_name: `+ Tambah "${params.inputValue}"`,
                    _id: 'add-new'
                  });
                }
                return filtered;
              }}
              renderInput={(params) => (
                <TextField {...params} label="Level Pekerjaan" placeholder="Cari level" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              fullWidth
              options={businessUnits}
              getOptionLabel={(option) => option?.bu_name || ''}
              value={businessUnits.find(b => b._id === employeeFormData.id_bu) || null}
              onChange={(_, newValue) => {
                if (newValue?.isAddNew) {
                  setOpenAddBuDialog(true);
                } else {
                  setEmployeeFormData({
                    ...employeeFormData,
                    id_bu: newValue?._id || ''
                  });
                }
              }}
              filterOptions={(options, params) => {
                const filtered = options.filter(option => 
                  option.bu_name?.toLowerCase().includes(params.inputValue.toLowerCase())
                );
                if (params.inputValue !== '' && filtered.length === 0) {
                  filtered.push({
                    isAddNew: true,
                    bu_name: `+ Tambah "${params.inputValue}"`,
                    _id: 'add-new'
                  });
                }
                return filtered;
              }}
              renderInput={(params) => (
                <TextField {...params} label="Unit Bisnis" placeholder="Cari unit bisnis" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              fullWidth
              options={offices}
              getOptionLabel={(option) => option?.office_name || ''}
              value={offices.find(o => o._id === employeeFormData.id_office) || null}
              onChange={(_, newValue) => {
                if (newValue?.isAddNew) {
                  setOpenAddOfficeDialog(true);
                } else {
                  setEmployeeFormData({
                    ...employeeFormData,
                    id_office: newValue?._id || ''
                  });
                }
              }}
              filterOptions={(options, params) => {
                const filtered = options.filter(option => 
                  option.office_name?.toLowerCase().includes(params.inputValue.toLowerCase())
                );
                if (params.inputValue !== '' && filtered.length === 0) {
                  filtered.push({
                    isAddNew: true,
                    office_name: `+ Tambah "${params.inputValue}"`,
                    _id: 'add-new'
                  });
                }
                return filtered;
              }}
              renderInput={(params) => (
                <TextField {...params} label="Kantor" placeholder="Cari kantor" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Tanggal Bergabung"
              type="date"
              value={employeeFormData.hire_date}
              onChange={(e) => setEmployeeFormData({ ...employeeFormData, hire_date: e.target.value })}
              InputLabelProps={{ 
                shrink: true,
                sx: { 
                  px: 0.5
                }
              }}
              inputProps={{
                placeholder: ''
              }}
              sx={{
                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                  marginLeft: 'auto'
                }
              }}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderEducationLocationForm = () => {
    const candidate = candidateData || location.state?.candidateData;
    const highestEducation = candidate?.education && candidate.education.length > 0 
      ? candidate.education.reduce((highest, current) => {
          const currentLevel = current.jenjang || '';
          const highestLevel = highest.jenjang || '';
          // Priority: S3 > S2 > S1 > D3 > D2 > D1 > SMA > SMK > SMP > SD
          const levelPriority = {
            'S3': 10, 'S2': 9, 'S1': 8, 'D3': 7, 'D2': 6, 'D1': 5, 
            'SMA': 4, 'SMK': 3, 'SMP': 2, 'SD': 1
          };
          return (levelPriority[currentLevel] || 0) > (levelPriority[highestLevel] || 0) ? current : highest;
        })
      : null;

    return (
      <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 3 }}>
        <CardHeader
          avatar={
            <Avatar sx={{ bgcolor: 'secondary.main' }}>
              <SchoolIcon />
            </Avatar>
          }
          title="Pendidikan & Lokasi"
          titleTypographyProps={{ fontWeight: 600 }}
        />
        <CardContent>
          <Grid container spacing={3}>
            {candidate && (
              <Grid item xs={12}>
                <Box sx={{ mb: 2, p: 2, bgcolor: 'info.50', borderRadius: 2, border: '1px solid', borderColor: 'info.200' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'info.main' }}>
                    Data Pendidikan dari Kandidat:
                  </Typography>
                  {candidate.education && candidate.education.length > 0 ? (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                        Total: {candidate.education.length} riwayat pendidikan ditemukan
                      </Typography>
                      <Grid container spacing={2}>
                        {candidate.education.map((edu, index) => (
                          <Grid item xs={12} md={6} key={index}>
                            <Box sx={{ 
                              p: 2, 
                              border: '1px solid', 
                              borderRadius: 2,
                              bgcolor: index === 0 ? 'primary.50' : 'grey.50',
                              borderColor: index === 0 ? 'primary.200' : 'grey.300'
                            }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                {index === 0 ? '🏆 Pendidikan Tertinggi' : `Pendidikan ${index + 1}`}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }}>
                                {edu.jenjang}: {edu.nama_sekolah}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Tahun: {edu.tahun_lulus}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Tempat: {edu.tempat}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Nilai: {edu.rata_rata_nilai}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Jurusan: {edu.jurusan || 'Tidak ada jurusan'}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Tidak ada data pendidikan
                    </Typography>
                  )}
                </Box>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Perguruan Tinggi"
                value={employeeFormData.collage || highestEducation?.nama_sekolah || ''}
                onChange={(e) => setEmployeeFormData({ ...employeeFormData, collage: e.target.value })}
                helperText={highestEducation ? `Dari kandidat (${highestEducation.jenjang}): ${highestEducation.nama_sekolah}` : ''}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Tingkat Pendidikan"
                value={employeeFormData.education || highestEducation?.jenjang || ''}
                onChange={(e) => setEmployeeFormData({ ...employeeFormData, education: e.target.value })}
                helperText={highestEducation ? `Dari kandidat: ${highestEducation.jenjang}` : ''}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Jurusan"
                value={employeeFormData.education_major || highestEducation?.jurusan || ''}
                onChange={(e) => setEmployeeFormData({ ...employeeFormData, education_major: e.target.value })}
                helperText={highestEducation ? `Dari kandidat: ${highestEducation.jurusan || 'Tidak ada jurusan'}` : ''}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Grade"
                value={employeeFormData.grade}
                onChange={(e) => setEmployeeFormData({ ...employeeFormData, grade: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Alamat"
                multiline
                rows={2}
                value={employeeFormData.address || candidate?.personalData?.address || ''}
                onChange={(e) => setEmployeeFormData({ ...employeeFormData, address: e.target.value })}
                helperText={candidate?.personalData?.address ? `Dari kandidat: ${candidate.personalData.address}` : ''}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="ID Kecamatan"
                value={employeeFormData.id_subdistrict}
                onChange={(e) => setEmployeeFormData({ ...employeeFormData, id_subdistrict: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="ID Kota"
                value={employeeFormData.id_city}
                onChange={(e) => setEmployeeFormData({ ...employeeFormData, id_city: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="ID Provinsi"
                value={employeeFormData.id_province}
                onChange={(e) => setEmployeeFormData({ ...employeeFormData, id_province: e.target.value })}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const renderAdditionalInfoForm = () => (
    <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 3 }}>
      <CardHeader
        avatar={
          <Avatar sx={{ bgcolor: 'warning.main' }}>
            <LocationIcon />
          </Avatar>
        }
        title="Informasi Tambahan"
        titleTypographyProps={{ fontWeight: 600 }}
      />
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="BPJS TK"
              value={employeeFormData.bpjs_tk}
              onChange={(e) => setEmployeeFormData({ ...employeeFormData, bpjs_tk: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="BPJS Kesehatan"
              value={employeeFormData.bpjs_kes}
              onChange={(e) => setEmployeeFormData({ ...employeeFormData, bpjs_kes: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Kategori Pajak"
              value={employeeFormData.tax_category}
              onChange={(e) => setEmployeeFormData({ ...employeeFormData, tax_category: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="PPh21"
              value={employeeFormData.pph21}
              onChange={(e) => setEmployeeFormData({ ...employeeFormData, pph21: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Tanggal Lahir"
              type="date"
              value={employeeFormData.birth_date || candidateData?.personalData?.birth_date || ''}
              onChange={(e) => setEmployeeFormData({ ...employeeFormData, birth_date: e.target.value })}
              InputLabelProps={{ 
                shrink: true,
                sx: { 
                  px: 0.5
                }
              }}
              inputProps={{
                placeholder: ''
              }}
              sx={{
                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                  marginLeft: 'auto'
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Tanggal Ditambahkan"
              type="date"
              value={employeeFormData.date_added}
              onChange={(e) => setEmployeeFormData({ ...employeeFormData, date_added: e.target.value })}
              InputLabelProps={{ 
                shrink: true,
                sx: { 
                  px: 0.5
                }
              }}
              inputProps={{
                placeholder: ''
              }}
              sx={{
                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                  marginLeft: 'auto'
                }
              }}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderCandidateHistoryForm = () => (
    <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 3 }}>
      <CardHeader
        avatar={
          <Avatar sx={{ bgcolor: 'success.main' }}>
            <WorkIcon />
          </Avatar>
        }
        title="Data Kandidat - Riwayat"
        titleTypographyProps={{ fontWeight: 600 }}
      />
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Tempat Lahir"
              value={employeeFormData.staticData.personalData.birth_place}
              onChange={(e) => setEmployeeFormData({
                ...employeeFormData,
                staticData: {
                  ...employeeFormData.staticData,
                  personalData: {
                    ...employeeFormData.staticData.personalData,
                    birth_place: e.target.value
                  }
                }
              })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Agama"
              value={employeeFormData.staticData.personalData.religion}
              onChange={(e) => setEmployeeFormData({
                ...employeeFormData,
                staticData: {
                  ...employeeFormData.staticData,
                  personalData: {
                    ...employeeFormData.staticData.personalData,
                    religion: e.target.value
                  }
                }
              })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Suku"
              value={employeeFormData.staticData.personalData.ethnicity}
              onChange={(e) => setEmployeeFormData({
                ...employeeFormData,
                staticData: {
                  ...employeeFormData.staticData,
                  personalData: {
                    ...employeeFormData.staticData.personalData,
                    ethnicity: e.target.value
                  }
                }
              })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Usia"
              type="number"
              value={employeeFormData.staticData.personalData.age}
              onChange={(e) => setEmployeeFormData({
                ...employeeFormData,
                staticData: {
                  ...employeeFormData.staticData,
                  personalData: {
                    ...employeeFormData.staticData.personalData,
                    age: e.target.value
                  }
                }
              })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Tinggi (cm)"
              type="number"
              value={employeeFormData.staticData.personalData.height_cm}
              onChange={(e) => setEmployeeFormData({
                ...employeeFormData,
                staticData: {
                  ...employeeFormData.staticData,
                  personalData: {
                    ...employeeFormData.staticData.personalData,
                    height_cm: e.target.value
                  }
                }
              })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Berat (kg)"
              type="number"
              value={employeeFormData.staticData.personalData.weight_kg}
              onChange={(e) => setEmployeeFormData({
                ...employeeFormData,
                staticData: {
                  ...employeeFormData.staticData,
                  personalData: {
                    ...employeeFormData.staticData.personalData,
                    weight_kg: e.target.value
                  }
                }
              })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Alasan Keluar Kerja Terakhir"
              value={employeeFormData.staticData.history.last_job_reason_leave}
              onChange={(e) => setEmployeeFormData({
                ...employeeFormData,
                staticData: {
                  ...employeeFormData.staticData,
                  history: {
                    ...employeeFormData.staticData.history,
                    last_job_reason_leave: e.target.value
                  }
                }
              })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Gaji Terakhir"
              value={employeeFormData.staticData.history.last_job_salary}
              onChange={(e) => setEmployeeFormData({
                ...employeeFormData,
                staticData: {
                  ...employeeFormData.staticData,
                  history: {
                    ...employeeFormData.staticData.history,
                    last_job_salary: e.target.value
                  }
                }
              })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Hobi"
              value={employeeFormData.staticData.history.hobby}
              onChange={(e) => setEmployeeFormData({
                ...employeeFormData,
                staticData: {
                  ...employeeFormData.staticData,
                  history: {
                    ...employeeFormData.staticData.history,
                    hobby: e.target.value
                  }
                }
              })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Olahraga"
              value={employeeFormData.staticData.history.sport}
              onChange={(e) => setEmployeeFormData({
                ...employeeFormData,
                staticData: {
                  ...employeeFormData.staticData,
                  history: {
                    ...employeeFormData.staticData.history,
                    sport: e.target.value
                  }
                }
              })}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const addFamilyMember = () => {
    const newMember = {
      type: '',
      name: '',
      gender: '',
      age: '',
      education: '',
      position: '',
      job: '',
      company: '',
      notes: ''
    };
    setEmployeeFormData({
      ...employeeFormData,
      staticData: {
        ...employeeFormData.staticData,
        family: [...employeeFormData.staticData.family, newMember]
      }
    });
  };

  const removeFamilyMember = (index) => {
    const updatedFamily = employeeFormData.staticData.family.filter((_, i) => i !== index);
    setEmployeeFormData({
      ...employeeFormData,
      staticData: {
        ...employeeFormData.staticData,
        family: updatedFamily
      }
    });
  };

  const updateFamilyMember = (index, field, value) => {
    const updatedFamily = employeeFormData.staticData.family.map((member, i) => 
      i === index ? { ...member, [field]: value } : member
    );
    setEmployeeFormData({
      ...employeeFormData,
      staticData: {
        ...employeeFormData.staticData,
        family: updatedFamily
      }
    });
  };

  const renderCandidateFamilyForm = () => (
    <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 3 }}>
      <CardHeader
        avatar={
          <Avatar sx={{ bgcolor: 'warning.main' }}>
            <PersonIcon />
          </Avatar>
        }
        title="Data Kandidat - Keluarga"
        titleTypographyProps={{ fontWeight: 600 }}
      />
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Status Pernikahan"
              value={employeeFormData.staticData.personalData.marital_status}
              onChange={(e) => setEmployeeFormData({
                ...employeeFormData,
                staticData: {
                  ...employeeFormData.staticData,
                  personalData: {
                    ...employeeFormData.staticData.personalData,
                    marital_status: e.target.value
                  }
                }
              })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Tanggal Menikah"
              type="date"
              value={employeeFormData.staticData.personalData.marital_date}
              onChange={(e) => setEmployeeFormData({
                ...employeeFormData,
                staticData: {
                  ...employeeFormData.staticData,
                  personalData: {
                    ...employeeFormData.staticData.personalData,
                    marital_date: e.target.value
                  }
                }
              })}
              InputLabelProps={{ 
                shrink: true,
                sx: { 
                  px: 0.5
                }
              }}
              inputProps={{
                placeholder: ''
              }}
              sx={{
                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                  marginLeft: 'auto'
                }
              }}
              disabled={employeeFormData.staticData.personalData.marital_status === 'Single'}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nama Gadis Ibu"
              value={employeeFormData.staticData.personalData.mother_maiden_name}
              onChange={(e) => setEmployeeFormData({
                ...employeeFormData,
                staticData: {
                  ...employeeFormData.staticData,
                  personalData: {
                    ...employeeFormData.staticData.personalData,
                    mother_maiden_name: e.target.value
                  }
                }
              })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Status Tinggal"
              value={employeeFormData.staticData.personalData.living_status}
              onChange={(e) => setEmployeeFormData({
                ...employeeFormData,
                staticData: {
                  ...employeeFormData.staticData,
                  personalData: {
                    ...employeeFormData.staticData.personalData,
                    living_status: e.target.value
                  }
                }
              })}
            />
          </Grid>
          
          {/* Data Keluarga Multiple */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Data Keluarga
              </Typography>
              <Button
                variant="contained"
                color="success"
                size="small"
                onClick={addFamilyMember}
                startIcon={<AddIcon />}
                sx={{ borderRadius: 2 }}
              >
                Tambah Keluarga
              </Button>
            </Box>
            
            {employeeFormData.staticData.family.map((member, index) => (
              <Card key={index} sx={{ mb: 2, p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Jenis Keluarga</InputLabel>
                      <Select
                        value={member.type}
                        onChange={(e) => updateFamilyMember(index, 'type', e.target.value)}
                      >
                        <MenuItem value="Suami/Istri">Suami/Istri</MenuItem>
                        <MenuItem value="Anak">Anak</MenuItem>
                        <MenuItem value="Orang Tua">Orang Tua</MenuItem>
                        <MenuItem value="Saudara">Saudara</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Nama"
                      value={member.name}
                      onChange={(e) => updateFamilyMember(index, 'name', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>L/P</InputLabel>
                      <Select
                        value={member.gender}
                        onChange={(e) => updateFamilyMember(index, 'gender', e.target.value)}
                      >
                        <MenuItem value="L">Laki-laki</MenuItem>
                        <MenuItem value="P">Perempuan</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Usia"
                      type="number"
                      value={member.age}
                      onChange={(e) => updateFamilyMember(index, 'age', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <IconButton
                      color="error"
                      onClick={() => removeFamilyMember(index)}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Pendidikan"
                      value={member.education}
                      onChange={(e) => updateFamilyMember(index, 'education', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Pekerjaan"
                      value={member.job}
                      onChange={(e) => updateFamilyMember(index, 'job', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Perusahaan"
                      value={member.company}
                      onChange={(e) => updateFamilyMember(index, 'company', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Keterangan"
                      value={member.notes}
                      onChange={(e) => updateFamilyMember(index, 'notes', e.target.value)}
                    />
                  </Grid>
                </Grid>
              </Card>
            ))}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  // Functions for Education Management
  const addEducation = () => {
    const newEducation = {
      level: '',
      school_name: '',
      location: '',
      graduation_year: '',
      gpa: '',
      major: ''
    };
    setEmployeeFormData({
      ...employeeFormData,
      staticData: {
        ...employeeFormData.staticData,
        education: [...employeeFormData.staticData.education, newEducation]
      }
    });
  };

  const removeEducation = (index) => {
    const updatedEducation = employeeFormData.staticData.education.filter((_, i) => i !== index);
    setEmployeeFormData({
      ...employeeFormData,
      staticData: {
        ...employeeFormData.staticData,
        education: updatedEducation
      }
    });
  };

  const updateEducation = (index, field, value) => {
    const updatedEducation = employeeFormData.staticData.education.map((edu, i) => 
      i === index ? { ...edu, [field]: value } : edu
    );
    setEmployeeFormData({
      ...employeeFormData,
      staticData: {
        ...employeeFormData.staticData,
        education: updatedEducation
      }
    });
  };

  // Functions for Work History Management
  const addWorkHistory = () => {
    const newWork = {
      entry_date: '',
      exit_date: '',
      position: '',
      company: '',
      address_phone: '',
      notes: ''
    };
    setEmployeeFormData({
      ...employeeFormData,
      staticData: {
        ...employeeFormData.staticData,
        workHistory: [...employeeFormData.staticData.workHistory, newWork]
      }
    });
  };

  const removeWorkHistory = (index) => {
    const updatedWorkHistory = employeeFormData.staticData.workHistory.filter((_, i) => i !== index);
    setEmployeeFormData({
      ...employeeFormData,
      staticData: {
        ...employeeFormData.staticData,
        workHistory: updatedWorkHistory
      }
    });
  };

  const updateWorkHistory = (index, field, value) => {
    const updatedWorkHistory = employeeFormData.staticData.workHistory.map((work, i) => 
      i === index ? { ...work, [field]: value } : work
    );
    setEmployeeFormData({
      ...employeeFormData,
      staticData: {
        ...employeeFormData.staticData,
        workHistory: updatedWorkHistory
      }
    });
  };

  // Functions for Courses Management
  const addCourse = () => {
    const newCourse = {
      instructor_institution: '',
      location: '',
      duration: '',
      funded_by: '',
      notes: ''
    };
    setEmployeeFormData({
      ...employeeFormData,
      staticData: {
        ...employeeFormData.staticData,
        courses: [...employeeFormData.staticData.courses, newCourse]
      }
    });
  };

  const removeCourse = (index) => {
    const updatedCourses = employeeFormData.staticData.courses.filter((_, i) => i !== index);
    setEmployeeFormData({
      ...employeeFormData,
      staticData: {
        ...employeeFormData.staticData,
        courses: updatedCourses
      }
    });
  };

  const updateCourse = (index, field, value) => {
    const updatedCourses = employeeFormData.staticData.courses.map((course, i) => 
      i === index ? { ...course, [field]: value } : course
    );
    setEmployeeFormData({
      ...employeeFormData,
      staticData: {
        ...employeeFormData.staticData,
        courses: updatedCourses
      }
    });
  };

  // Functions for Social Activities Management
  const addSocialActivity = () => {
    const newActivity = {
      year: '',
      activity_type: '',
      position: '',
      organization: '',
      notes: ''
    };
    setEmployeeFormData({
      ...employeeFormData,
      staticData: {
        ...employeeFormData.staticData,
        socialActivities: [...employeeFormData.staticData.socialActivities, newActivity]
      }
    });
  };

  const removeSocialActivity = (index) => {
    const updatedActivities = employeeFormData.staticData.socialActivities.filter((_, i) => i !== index);
    setEmployeeFormData({
      ...employeeFormData,
      staticData: {
        ...employeeFormData.staticData,
        socialActivities: updatedActivities
      }
    });
  };

  const updateSocialActivity = (index, field, value) => {
    const updatedActivities = employeeFormData.staticData.socialActivities.map((activity, i) => 
      i === index ? { ...activity, [field]: value } : activity
    );
    setEmployeeFormData({
      ...employeeFormData,
      staticData: {
        ...employeeFormData.staticData,
        socialActivities: updatedActivities
      }
    });
  };

  // Functions for Emergency Relations Management
  const addEmergencyRelation = () => {
    const newRelation = {
      name: '',
      age: '',
      address: '',
      phone: '',
      relationship: '',
      notes: ''
    };
    setEmployeeFormData({
      ...employeeFormData,
      staticData: {
        ...employeeFormData.staticData,
        emergency_relations: [...employeeFormData.staticData.emergency_relations, newRelation]
      }
    });
  };

  const removeEmergencyRelation = (index) => {
    const updatedRelations = employeeFormData.staticData.emergency_relations.filter((_, i) => i !== index);
    setEmployeeFormData({
      ...employeeFormData,
      staticData: {
        ...employeeFormData.staticData,
        emergency_relations: updatedRelations
      }
    });
  };

  const updateEmergencyRelation = (index, field, value) => {
    const updatedRelations = employeeFormData.staticData.emergency_relations.map((relation, i) => 
      i === index ? { ...relation, [field]: value } : relation
    );
    setEmployeeFormData({
      ...employeeFormData,
      staticData: {
        ...employeeFormData.staticData,
        emergency_relations: updatedRelations
      }
    });
  };

  // Functions for Former Relations Management
  const addFormerRelation = () => {
    const newRelation = {
      name: '',
      age: '',
      address: '',
      phone: '',
      relationship: '',
      notes: ''
    };
    setEmployeeFormData({
      ...employeeFormData,
      staticData: {
        ...employeeFormData.staticData,
        former_relations: [...employeeFormData.staticData.former_relations, newRelation]
      }
    });
  };

  const removeFormerRelation = (index) => {
    const updatedRelations = employeeFormData.staticData.former_relations.filter((_, i) => i !== index);
    setEmployeeFormData({
      ...employeeFormData,
      staticData: {
        ...employeeFormData.staticData,
        former_relations: updatedRelations
      }
    });
  };

  const updateFormerRelation = (index, field, value) => {
    const updatedRelations = employeeFormData.staticData.former_relations.map((relation, i) => 
      i === index ? { ...relation, [field]: value } : relation
    );
    setEmployeeFormData({
      ...employeeFormData,
      staticData: {
        ...employeeFormData.staticData,
        former_relations: updatedRelations
      }
    });
  };

  // Functions for Guarantors Management
  const addGuarantor = () => {
    const newGuarantor = {
      name: '',
      age: '',
      address: '',
      phone: '',
      relationship: '',
      notes: ''
    };
    setEmployeeFormData({
      ...employeeFormData,
      staticData: {
        ...employeeFormData.staticData,
        guarantors: [...employeeFormData.staticData.guarantors, newGuarantor]
      }
    });
  };

  const removeGuarantor = (index) => {
    const updatedGuarantors = employeeFormData.staticData.guarantors.filter((_, i) => i !== index);
    setEmployeeFormData({
      ...employeeFormData,
      staticData: {
        ...employeeFormData.staticData,
        guarantors: updatedGuarantors
      }
    });
  };

  const updateGuarantor = (index, field, value) => {
    const updatedGuarantors = employeeFormData.staticData.guarantors.map((guarantor, i) => 
      i === index ? { ...guarantor, [field]: value } : guarantor
    );
    setEmployeeFormData({
      ...employeeFormData,
      staticData: {
        ...employeeFormData.staticData,
        guarantors: updatedGuarantors
      }
    });
  };

  const renderCandidateSkillsForm = () => (
    <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 3 }}>
      <CardHeader
        avatar={
          <Avatar sx={{ bgcolor: 'secondary.main' }}>
            <WorkIcon />
          </Avatar>
        }
        title="Data Kandidat - Kemampuan"
        titleTypographyProps={{ fontWeight: 600 }}
      />
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Alasan Melamar"
              multiline
              rows={2}
              value={employeeFormData.staticData.applicationSkills.apply_reason}
              onChange={(e) => setEmployeeFormData({
                ...employeeFormData,
                staticData: {
                  ...employeeFormData.staticData,
                  applicationSkills: {
                    ...employeeFormData.staticData.applicationSkills,
                    apply_reason: e.target.value
                  }
                }
              })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Posisi yang Diinginkan"
              value={employeeFormData.staticData.applicationSkills.desired_position}
              onChange={(e) => setEmployeeFormData({
                ...employeeFormData,
                staticData: {
                  ...employeeFormData.staticData,
                  applicationSkills: {
                    ...employeeFormData.staticData.applicationSkills,
                    desired_position: e.target.value
                  }
                }
              })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Gaji yang Diharapkan"
              value={employeeFormData.staticData.applicationSkills.expected_salary}
              onChange={(e) => setEmployeeFormData({
                ...employeeFormData,
                staticData: {
                  ...employeeFormData.staticData,
                  applicationSkills: {
                    ...employeeFormData.staticData.applicationSkills,
                    expected_salary: e.target.value
                  }
                }
              })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Keahlian"
              value={employeeFormData.staticData.applicationSkills.skills}
              onChange={(e) => setEmployeeFormData({
                ...employeeFormData,
                staticData: {
                  ...employeeFormData.staticData,
                  applicationSkills: {
                    ...employeeFormData.staticData.applicationSkills,
                    skills: e.target.value
                  }
                }
              })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Program Komputer"
              value={employeeFormData.staticData.applicationSkills.computer_programs}
              onChange={(e) => setEmployeeFormData({
                ...employeeFormData,
                staticData: {
                  ...employeeFormData.staticData,
                  applicationSkills: {
                    ...employeeFormData.staticData.applicationSkills,
                    computer_programs: e.target.value
                  }
                }
              })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Kemampuan Mengemudi"
              value={employeeFormData.staticData.applicationSkills.driving_ability}
              onChange={(e) => setEmployeeFormData({
                ...employeeFormData,
                staticData: {
                  ...employeeFormData.staticData,
                  applicationSkills: {
                    ...employeeFormData.staticData.applicationSkills,
                    driving_ability: e.target.value
                  }
                }
              })}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderCandidateHealthForm = () => (
    <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 3 }}>
      <CardHeader
        avatar={
          <Avatar sx={{ bgcolor: 'error.main' }}>
            <PersonIcon />
          </Avatar>
        }
        title="Data Kandidat - Kesehatan"
        titleTypographyProps={{ fontWeight: 600 }}
      />
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Penyakit Umum"
              value={employeeFormData.staticData.generalHealth.common_illness}
              onChange={(e) => setEmployeeFormData({
                ...employeeFormData,
                staticData: {
                  ...employeeFormData.staticData,
                  generalHealth: {
                    ...employeeFormData.staticData.generalHealth,
                    common_illness: e.target.value
                  }
                }
              })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Pemeriksaan Terakhir"
              type="date"
              value={employeeFormData.staticData.generalHealth.last_checkup}
              onChange={(e) => setEmployeeFormData({
                ...employeeFormData,
                staticData: {
                  ...employeeFormData.staticData,
                  generalHealth: {
                    ...employeeFormData.staticData.generalHealth,
                    last_checkup: e.target.value
                  }
                }
              })}
              InputLabelProps={{ 
                shrink: true,
                sx: { 
                  px: 0.5
                }
              }}
              inputProps={{
                placeholder: ''
              }}
              sx={{
                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                  marginLeft: 'auto'
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Kekuatan"
              multiline
              rows={2}
              value={employeeFormData.staticData.generalHealth.strength}
              onChange={(e) => setEmployeeFormData({
                ...employeeFormData,
                staticData: {
                  ...employeeFormData.staticData,
                  generalHealth: {
                    ...employeeFormData.staticData.generalHealth,
                    strength: e.target.value
                  }
                }
              })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Kelemahan"
              multiline
              rows={2}
              value={employeeFormData.staticData.generalHealth.weakness}
              onChange={(e) => setEmployeeFormData({
                ...employeeFormData,
                staticData: {
                  ...employeeFormData.staticData,
                  generalHealth: {
                    ...employeeFormData.staticData.generalHealth,
                    weakness: e.target.value
                  }
                }
              })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Reaksi terhadap Kritik"
              value={employeeFormData.staticData.generalHealth.reaction_to_criticism}
              onChange={(e) => setEmployeeFormData({
                ...employeeFormData,
                staticData: {
                  ...employeeFormData.staticData,
                  generalHealth: {
                    ...employeeFormData.staticData.generalHealth,
                    reaction_to_criticism: e.target.value
                  }
                }
              })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Prestasi Terbaik"
              value={employeeFormData.staticData.personalData.best_achievement}
              onChange={(e) => setEmployeeFormData({
                ...employeeFormData,
                staticData: {
                  ...employeeFormData.staticData,
                  personalData: {
                    ...employeeFormData.staticData.personalData,
                    best_achievement: e.target.value
                  }
                }
              })}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  // Render Education Form
  const renderEducationForm = () => (
    <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 3 }}>
      <CardHeader
        avatar={
          <Avatar sx={{ bgcolor: 'info.main' }}>
            <SchoolIcon />
          </Avatar>
        }
        title="Pendidikan Formal"
        titleTypographyProps={{ fontWeight: 600 }}
      />
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Riwayat Pendidikan
          </Typography>
          <Button
            variant="contained"
            color="info"
            size="small"
            onClick={addEducation}
            startIcon={<AddIcon />}
            sx={{ borderRadius: 2 }}
          >
            Tambah Pendidikan
          </Button>
        </Box>
        
        {employeeFormData.staticData.education.map((edu, index) => (
          <Card key={index} sx={{ mb: 2, p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Jenjang</InputLabel>
                  <Select
                    value={edu.level}
                    onChange={(e) => updateEducation(index, 'level', e.target.value)}
                  >
                    <MenuItem value="SD">SD</MenuItem>
                    <MenuItem value="SMP">SMP</MenuItem>
                    <MenuItem value="SMA">SMA/SMK</MenuItem>
                    <MenuItem value="D3">D3</MenuItem>
                    <MenuItem value="S1">S1</MenuItem>
                    <MenuItem value="S2">S2</MenuItem>
                    <MenuItem value="S3">S3</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Nama Sekolah"
                  value={edu.school_name}
                  onChange={(e) => updateEducation(index, 'school_name', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Lokasi"
                  value={edu.location}
                  onChange={(e) => updateEducation(index, 'location', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Tahun Lulus"
                  type="number"
                  value={edu.graduation_year}
                  onChange={(e) => updateEducation(index, 'graduation_year', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <IconButton
                  color="error"
                  onClick={() => removeEducation(index)}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="IPK/Nilai"
                  value={edu.gpa}
                  onChange={(e) => updateEducation(index, 'gpa', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={9}>
                <TextField
                  fullWidth
                  size="small"
                  label="Jurusan"
                  value={edu.major}
                  onChange={(e) => updateEducation(index, 'major', e.target.value)}
                />
              </Grid>
            </Grid>
          </Card>
        ))}
      </CardContent>
    </Card>
  );

  // Render Work History Form
  const renderWorkHistoryForm = () => (
    <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 3 }}>
      <CardHeader
        avatar={
          <Avatar sx={{ bgcolor: 'success.main' }}>
            <WorkIcon />
          </Avatar>
        }
        title="Riwayat Pekerjaan"
        titleTypographyProps={{ fontWeight: 600 }}
      />
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Pengalaman Kerja
          </Typography>
          <Button
            variant="contained"
            color="success"
            size="small"
            onClick={addWorkHistory}
            startIcon={<AddIcon />}
            sx={{ borderRadius: 2 }}
          >
            Tambah Pekerjaan
          </Button>
        </Box>
        
        {employeeFormData.staticData.workHistory.map((work, index) => (
          <Card key={index} sx={{ mb: 2, p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Tanggal Masuk"
                  type="date"
                  value={work.entry_date}
                  onChange={(e) => updateWorkHistory(index, 'entry_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Tanggal Keluar"
                  type="date"
                  value={work.exit_date}
                  onChange={(e) => updateWorkHistory(index, 'exit_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Jabatan"
                  value={work.position}
                  onChange={(e) => updateWorkHistory(index, 'position', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <IconButton
                  color="error"
                  onClick={() => removeWorkHistory(index)}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Perusahaan"
                  value={work.company}
                  onChange={(e) => updateWorkHistory(index, 'company', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Alamat & Telepon"
                  value={work.address_phone}
                  onChange={(e) => updateWorkHistory(index, 'address_phone', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Keterangan"
                  value={work.notes}
                  onChange={(e) => updateWorkHistory(index, 'notes', e.target.value)}
                />
              </Grid>
            </Grid>
          </Card>
        ))}
      </CardContent>
    </Card>
  );

  // Render Courses Form
  const renderCoursesForm = () => (
    <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 3 }}>
      <CardHeader
        avatar={
          <Avatar sx={{ bgcolor: 'warning.main' }}>
            <SchoolIcon />
          </Avatar>
        }
        title="Kursus & Pelatihan"
        titleTypographyProps={{ fontWeight: 600 }}
      />
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Riwayat Kursus
          </Typography>
          <Button
            variant="contained"
            color="warning"
            size="small"
            onClick={addCourse}
            startIcon={<AddIcon />}
            sx={{ borderRadius: 2 }}
          >
            Tambah Kursus
          </Button>
        </Box>
        
        {employeeFormData.staticData.courses.map((course, index) => (
          <Card key={index} sx={{ mb: 2, p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Instruktur/Lembaga"
                  value={course.instructor_institution}
                  onChange={(e) => updateCourse(index, 'instructor_institution', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Lokasi"
                  value={course.location}
                  onChange={(e) => updateCourse(index, 'location', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Durasi"
                  value={course.duration}
                  onChange={(e) => updateCourse(index, 'duration', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Dibiayai"
                  value={course.funded_by}
                  onChange={(e) => updateCourse(index, 'funded_by', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Keterangan"
                  value={course.notes}
                  onChange={(e) => updateCourse(index, 'notes', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={1}>
                <IconButton
                  color="error"
                  onClick={() => removeCourse(index)}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Card>
        ))}
      </CardContent>
    </Card>
  );

  // Render Simple Mode Forms
  const renderSimpleEmployeeIdForm = () => (
    <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 3 }}>
      <CardHeader
        avatar={
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <PersonIcon />
          </Avatar>
        }
        title="ID Karyawan & Foto"
        titleTypographyProps={{ fontWeight: 600 }}
      />
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="ID Karyawan *"
              value={employeeFormData.emp_id}
              onChange={(e) => setEmployeeFormData(prev => ({ ...prev, emp_id: e.target.value }))}
              required
              sx={{ mb: 2 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Foto Karyawan
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  variant="outlined"
                  component="label"
                  sx={{ 
                    borderStyle: 'dashed',
                    borderWidth: 2,
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    '&:hover': {
                      borderColor: 'primary.dark',
                      backgroundColor: 'primary.50'
                    }
                  }}
                >
                  Pilih File
                  <Input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png"
                    onChange={handlePhotoUpload}
                    sx={{ display: 'none' }}
                  />
                </Button>
                {photoFile && (
                  <Typography variant="body2" color="success.main">
                    {photoFile.name}
                  </Typography>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                Format: JPG, PNG | Maksimal: 5MB
              </Typography>
            </Box>
          </Grid>
          {photoPreview && (
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Preview Foto:
                </Typography>
                <Avatar
                  src={photoPreview}
                  sx={{ 
                    width: 150, 
                    height: 150, 
                    mx: 'auto',
                    border: '2px solid',
                    borderColor: 'primary.main'
                  }}
                />
              </Box>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );

  const renderSimpleNameForm = () => {
    const candidate = candidateData || location.state?.candidateData;
    const fullName = candidate?.personalData?.full_name || '';
    const nameParts = fullName.split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts[nameParts.length - 1] || '';
    const middleName = nameParts.slice(1, -1).join(' ') || '';

    return (
      <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 3 }}>
        <CardHeader
          avatar={
            <Avatar sx={{ bgcolor: 'secondary.main' }}>
              <PersonIcon />
            </Avatar>
          }
          title="Nama Lengkap"
          titleTypographyProps={{ fontWeight: 600 }}
        />
        <CardContent>
          <Grid container spacing={3}>
            {candidate && (
              <Grid item xs={12}>
                <Box sx={{ mb: 2, p: 2, bgcolor: 'info.50', borderRadius: 2, border: '1px solid', borderColor: 'info.200' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'info.main' }}>
                    Data dari kandidat:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {fullName}
                  </Typography>
                </Box>
              </Grid>
            )}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Nama Depan *"
                value={employeeFormData.staticData?.personalData?.first_name || firstName}
                onChange={(e) => {
                  const firstName = e.target.value;
                  const lastName = employeeFormData.staticData?.personalData?.last_name || '';
                  const fullName = `${firstName} ${lastName}`.trim();
                  
                  setEmployeeFormData(prev => ({
                    ...prev,
                    full_name: fullName,
                    staticData: {
                      ...prev.staticData,
                      personalData: {
                        ...prev.staticData.personalData,
                        first_name: firstName
                      }
                    }
                  }));
                }}
                required
                sx={{ mb: 2 }}
                helperText={firstName ? `Dari kandidat: ${firstName}` : ''}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Nama Tengah"
                value={employeeFormData.staticData?.personalData?.middle_name || middleName}
                onChange={(e) => setEmployeeFormData(prev => ({
                  ...prev,
                  staticData: {
                    ...prev.staticData,
                    personalData: {
                      ...prev.staticData.personalData,
                      middle_name: e.target.value
                    }
                  }
                }))}
                sx={{ mb: 2 }}
                helperText={middleName ? `Dari kandidat: ${middleName}` : ''}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Nama Belakang *"
                value={employeeFormData.staticData?.personalData?.last_name || lastName}
                onChange={(e) => {
                  const lastName = e.target.value;
                  const firstName = employeeFormData.staticData?.personalData?.first_name || '';
                  const fullName = `${firstName} ${lastName}`.trim();
                  
                  setEmployeeFormData(prev => ({
                    ...prev,
                    full_name: fullName,
                    staticData: {
                      ...prev.staticData,
                      personalData: {
                        ...prev.staticData.personalData,
                        last_name: lastName
                      }
                    }
                  }));
                }}
                required
                sx={{ mb: 2 }}
                helperText={lastName ? `Dari kandidat: ${lastName}` : ''}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required sx={{ mb: 2 }}>
                <InputLabel>Jenis Kelamin *</InputLabel>
                <Select
                  value={employeeFormData.sex || candidate?.personalData?.gender || ''}
                  onChange={(e) => setEmployeeFormData(prev => ({ ...prev, sex: e.target.value }))}
                  label="Jenis Kelamin *"
                >
                  <MenuItem value="Male">Laki-laki</MenuItem>
                  <MenuItem value="Female">Perempuan</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email *"
                type="email"
                value={employeeFormData.email || candidate?.personalData?.email || ''}
                onChange={(e) => setEmployeeFormData(prev => ({ ...prev, email: e.target.value }))}
                required
                sx={{ mb: 2 }}
                helperText={candidate?.personalData?.email ? `Dari kandidat: ${candidate.personalData.email}` : ''}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nomor Telepon *"
                value={employeeFormData.no_telp || candidate?.personalData?.phone || ''}
                onChange={(e) => setEmployeeFormData(prev => ({ ...prev, no_telp: e.target.value }))}
                required
                sx={{ mb: 2 }}
                helperText={candidate?.personalData?.phone ? `Dari kandidat: ${candidate.personalData.phone}` : ''}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Alamat *"
                multiline
                rows={2}
                value={employeeFormData.address || candidate?.personalData?.address || ''}
                onChange={(e) => setEmployeeFormData(prev => ({ ...prev, address: e.target.value }))}
                required
                sx={{ mb: 2 }}
                helperText={candidate?.personalData?.address ? `Dari kandidat: ${candidate.personalData.address}` : ''}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const renderSimplePersonalInfoForm = () => {
    const candidate = candidateData || location.state?.candidateData;
    
    return (
      <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 3 }}>
        <CardHeader
          avatar={
            <Avatar sx={{ bgcolor: 'info.main' }}>
              <PersonIcon />
            </Avatar>
          }
          title="Informasi Personal"
          titleTypographyProps={{ fontWeight: 600 }}
        />
        <CardContent>
          <Grid container spacing={3}>
            {candidate && (
              <Grid item xs={12}>
                <Box sx={{ mb: 2, p: 2, bgcolor: 'info.50', borderRadius: 2, border: '1px solid', borderColor: 'info.200' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'info.main' }}>
                    Data dari kandidat:
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>Nama: {candidate.personalData?.full_name || '-'}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>Jenis Kelamin: {candidate.personalData?.gender || '-'}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>Tempat Lahir: {candidate.personalData?.birth_place || '-'}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>Tanggal Lahir: {candidate.personalData?.birth_date || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>Agama: {candidate.personalData?.religion || '-'}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>Suku: {candidate.personalData?.ethnicity || '-'}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>No. HP: {candidate.personalData?.phone || '-'}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>Alamat: {candidate.personalData?.address || '-'}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Tanggal Lahir"
                type="date"
                value={employeeFormData.staticData?.personalData?.birth_date || candidate?.personalData?.birth_date || ''}
                onChange={(e) => setEmployeeFormData(prev => ({
                  ...prev,
                  staticData: {
                    ...prev.staticData,
                    personalData: {
                      ...prev.staticData.personalData,
                      birth_date: e.target.value
                    }
                  }
                }))}
                InputLabelProps={{ 
                  shrink: true,
                  sx: { 
                    px: 0.5
                  }
                }}
                inputProps={{
                  placeholder: ''
                }}
                sx={{
                  mb: 2,
                  '& input[type="date"]::-webkit-calendar-picker-indicator': {
                    marginLeft: 'auto'
                  }
                }}
                helperText={candidate?.personalData?.birth_date ? `Dari kandidat: ${candidate.personalData.birth_date}` : ''}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Agama"
                value={employeeFormData.staticData?.personalData?.religion || candidate?.personalData?.religion || ''}
                onChange={(e) => setEmployeeFormData(prev => ({
                  ...prev,
                  staticData: {
                    ...prev.staticData,
                    personalData: {
                      ...prev.staticData.personalData,
                      religion: e.target.value
                    }
                  }
                }))}
                sx={{ mb: 2 }}
                helperText={candidate?.personalData?.religion ? `Dari kandidat: ${candidate.personalData.religion}` : ''}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Status Pernikahan</InputLabel>
                <Select
                  value={employeeFormData.staticData?.personalData?.marital_status || candidate?.family?.marital_status || ''}
                  onChange={(e) => setEmployeeFormData(prev => ({
                    ...prev,
                    staticData: {
                      ...prev.staticData,
                      personalData: {
                        ...prev.staticData.personalData,
                        marital_status: e.target.value
                      }
                    }
                  }))}
                  label="Status Pernikahan"
                >
                  <MenuItem value="Single">Single</MenuItem>
                  <MenuItem value="Married">Married</MenuItem>
                  <MenuItem value="Divorced">Divorced</MenuItem>
                  <MenuItem value="Widowed">Widowed</MenuItem>
                </Select>
                {candidate?.family?.marital_status && (
                  <Typography variant="caption" color="info.main" sx={{ mt: 0.5, display: 'block' }}>
                    Dari kandidat: {candidate.family.marital_status}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Tanggal Menikah"
                type="date"
                value={employeeFormData.staticData?.personalData?.marital_date || candidate?.family?.marriage_date || ''}
                onChange={(e) => setEmployeeFormData(prev => ({
                  ...prev,
                  staticData: {
                    ...prev.staticData,
                    personalData: {
                      ...prev.staticData.personalData,
                      marital_date: e.target.value
                    }
                  }
                }))}
                InputLabelProps={{
                  shrink: true,
                  sx: {
                    px: 0.5
                  }
                }}
                inputProps={{
                  placeholder: ''
                }}
                sx={{
                  mb: 2,
                  '& input[type="date"]::-webkit-calendar-picker-indicator': {
                    marginLeft: 'auto'
                  }
                }}
                helperText={candidate?.family?.marriage_date ? `Dari kandidat: ${candidate.family.marriage_date}` : ''}
                disabled={
                  (employeeFormData.staticData?.personalData?.marital_status === 'Single') ||
                  (!employeeFormData.staticData?.personalData?.marital_status && candidate?.family?.marital_status === 'Single')
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nama Ibu Kandung"
                value={employeeFormData.staticData?.personalData?.mother_maiden_name || ''}
                onChange={(e) => setEmployeeFormData(prev => ({
                  ...prev,
                  staticData: {
                    ...prev.staticData,
                    personalData: {
                      ...prev.staticData.personalData,
                      mother_maiden_name: e.target.value
                    }
                  }
                }))}
                sx={{ mb: 2 }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const renderSimpleWorkInfoForm = () => (
    <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 3 }}>
      <CardHeader
        avatar={
          <Avatar sx={{ bgcolor: 'success.main' }}>
            <WorkIcon />
          </Avatar>
        }
        title="Informasi Pekerjaan"
        titleTypographyProps={{ fontWeight: 600 }}
      />
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Autocomplete
              fullWidth
              options={businessUnits}
              getOptionLabel={(option) => option?.bu_name || ''}
              value={businessUnits.find(b => b._id === employeeFormData.id_bu) || null}
              onChange={(_, newValue) => {
                if (newValue?.isAddNew) {
                  setOpenAddBuDialog(true);
                } else {
                  setEmployeeFormData(prev => ({ ...prev, id_bu: newValue?._id || '' }));
                }
              }}
              disabled={masterDataLoading}
              filterOptions={(options, params) => {
                const filtered = options.filter(option => 
                  option.bu_name?.toLowerCase().includes(params.inputValue.toLowerCase())
                );
                if (params.inputValue !== '' && filtered.length === 0) {
                  filtered.push({
                    isAddNew: true,
                    bu_name: `+ Tambah "${params.inputValue}"`,
                    _id: 'add-new'
                  });
                }
                return filtered;
              }}
              renderInput={(params) => (
                <TextField {...params} label="Unit Bisnis" placeholder="Cari unit bisnis" sx={{ mb: 2 }} />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              fullWidth
              options={offices}
              getOptionLabel={(option) => option?.office_name || ''}
              value={offices.find(o => o._id === employeeFormData.id_office) || null}
              onChange={(_, newValue) => {
                if (newValue?.isAddNew) {
                  setOpenAddOfficeDialog(true);
                } else {
                  setEmployeeFormData(prev => ({ ...prev, id_office: newValue?._id || '' }));
                }
              }}
              disabled={masterDataLoading}
              filterOptions={(options, params) => {
                const filtered = options.filter(option => 
                  option.office_name?.toLowerCase().includes(params.inputValue.toLowerCase())
                );
                if (params.inputValue !== '' && filtered.length === 0) {
                  filtered.push({
                    isAddNew: true,
                    office_name: `+ Tambah "${params.inputValue}"`,
                    _id: 'add-new'
                  });
                }
                return filtered;
              }}
              renderInput={(params) => (
                <TextField {...params} label="Kantor" placeholder="Cari kantor" sx={{ mb: 2 }} />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              fullWidth
              options={departments}
              getOptionLabel={(option) => option?.department_name || ''}
              value={departments.find(d => d._id === employeeFormData.id_department) || null}
              onChange={(_, newValue) => {
                if (newValue?.isAddNew) {
                  setOpenAddDepartmentDialog(true);
                } else {
                  setEmployeeFormData(prev => ({ ...prev, id_department: newValue?._id || '' }));
                }
              }}
              disabled={masterDataLoading}
              filterOptions={(options, params) => {
                const filtered = options.filter(option => 
                  option.department_name?.toLowerCase().includes(params.inputValue.toLowerCase())
                );
                if (params.inputValue !== '' && filtered.length === 0) {
                  filtered.push({
                    isAddNew: true,
                    department_name: `+ Tambah "${params.inputValue}"`,
                    _id: 'add-new'
                  });
                }
                return filtered;
              }}
              renderInput={(params) => (
                <TextField {...params} label="Departemen *" placeholder="Cari departemen" required sx={{ mb: 2 }} />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              fullWidth
              options={jobLevels}
              getOptionLabel={(option) => option?.joblevel_name || ''}
              value={jobLevels.find(j => j._id === employeeFormData.id_job_level) || null}
              onChange={(_, newValue) => {
                if (newValue?.isAddNew) {
                  setOpenAddJoblevelDialog(true);
                } else {
                  setEmployeeFormData(prev => ({ ...prev, id_job_level: newValue?._id || '' }));
                }
              }}
              disabled={masterDataLoading}
              filterOptions={(options, params) => {
                const filtered = options.filter(option => 
                  option.joblevel_name?.toLowerCase().includes(params.inputValue.toLowerCase())
                );
                if (params.inputValue !== '' && filtered.length === 0) {
                  filtered.push({
                    isAddNew: true,
                    joblevel_name: `+ Tambah "${params.inputValue}"`,
                    _id: 'add-new'
                  });
                }
                return filtered;
              }}
              renderInput={(params) => (
                <TextField {...params} label="Level Pekerjaan" placeholder="Cari level" sx={{ mb: 2 }} />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              fullWidth
              options={designations}
              getOptionLabel={(option) => option?.designation_name || ''}
              value={designations.find(d => d._id === employeeFormData.id_designation) || null}
              onChange={(_, newValue) => {
                if (newValue?.isAddNew) {
                  setOpenAddDesignationDialog(true);
                } else {
                  setEmployeeFormData(prev => ({ ...prev, id_designation: newValue?._id || '' }));
                }
              }}
              disabled={masterDataLoading}
              filterOptions={(options, params) => {
                const filtered = options.filter(option => 
                  option.designation_name?.toLowerCase().includes(params.inputValue.toLowerCase())
                );
                if (params.inputValue !== '' && filtered.length === 0) {
                  filtered.push({
                    isAddNew: true,
                    designation_name: `+ Tambah "${params.inputValue}"`,
                    _id: 'add-new'
                  });
                }
                return filtered;
              }}
              renderInput={(params) => (
                <TextField {...params} label="Posisi *" placeholder="Cari posisi" required sx={{ mb: 2 }} />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Grade"
              value={employeeFormData.grade}
              onChange={(e) => setEmployeeFormData(prev => ({ ...prev, grade: e.target.value }))}
              sx={{ mb: 2 }}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderSimpleBenefitForm = () => (
    <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 3 }}>
      <CardHeader
        avatar={
          <Avatar sx={{ bgcolor: 'warning.main' }}>
            <LocationIcon />
          </Avatar>
        }
        title="Benefit & Pajak"
        titleTypographyProps={{ fontWeight: 600 }}
      />
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="BPJS TK"
              value={employeeFormData.bpjs_tk || ''}
              onChange={(e) => setEmployeeFormData(prev => ({ ...prev, bpjs_tk: e.target.value }))}
              sx={{ mb: 2 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="BPJS Kesehatan"
              value={employeeFormData.bpjs_kes || ''}
              onChange={(e) => setEmployeeFormData(prev => ({ ...prev, bpjs_kes: e.target.value }))}
              sx={{ mb: 2 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Kategori Pajak"
              value={employeeFormData.tax_category || ''}
              onChange={(e) => setEmployeeFormData(prev => ({ ...prev, tax_category: e.target.value }))}
              sx={{ mb: 2 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="PPh21"
              value={employeeFormData.pph21 || ''}
              onChange={(e) => setEmployeeFormData(prev => ({ ...prev, pph21: e.target.value }))}
              sx={{ mb: 2 }}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  // Render Relations Form
  const renderRelationsForm = () => (
    <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 3 }}>
      <CardHeader
        avatar={
          <Avatar sx={{ bgcolor: 'error.main' }}>
            <ContactsIcon />
          </Avatar>
        }
        title="Relasi & Penjamin"
        titleTypographyProps={{ fontWeight: 600 }}
      />
      <CardContent>
        {/* Emergency Relations */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Relasi Darurat
            </Typography>
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={addEmergencyRelation}
              startIcon={<AddIcon />}
              sx={{ borderRadius: 2 }}
            >
              Tambah Kontak Darurat
            </Button>
          </Box>
          
          {employeeFormData.staticData.emergency_relations.map((relation, index) => (
            <Card key={index} sx={{ mb: 2, p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Nama"
                    value={relation.name}
                    onChange={(e) => updateEmergencyRelation(index, 'name', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Usia"
                    type="number"
                    value={relation.age}
                    onChange={(e) => updateEmergencyRelation(index, 'age', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Alamat"
                    value={relation.address}
                    onChange={(e) => updateEmergencyRelation(index, 'address', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Telepon"
                    value={relation.phone}
                    onChange={(e) => updateEmergencyRelation(index, 'phone', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <IconButton
                    color="error"
                    onClick={() => removeEmergencyRelation(index)}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Hubungan"
                    value={relation.relationship}
                    onChange={(e) => updateEmergencyRelation(index, 'relationship', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Keterangan"
                    value={relation.notes}
                    onChange={(e) => updateEmergencyRelation(index, 'notes', e.target.value)}
                  />
                </Grid>
              </Grid>
            </Card>
          ))}
        </Box>

        {/* Former Relations */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Eks Atasan/Keluarga
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              onClick={addFormerRelation}
              startIcon={<AddIcon />}
              sx={{ borderRadius: 2 }}
            >
              Tambah Referensi
            </Button>
          </Box>
          
          {employeeFormData.staticData.former_relations.map((relation, index) => (
            <Card key={index} sx={{ mb: 2, p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Nama"
                    value={relation.name}
                    onChange={(e) => updateFormerRelation(index, 'name', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Usia"
                    type="number"
                    value={relation.age}
                    onChange={(e) => updateFormerRelation(index, 'age', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Alamat"
                    value={relation.address}
                    onChange={(e) => updateFormerRelation(index, 'address', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Telepon"
                    value={relation.phone}
                    onChange={(e) => updateFormerRelation(index, 'phone', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <IconButton
                    color="error"
                    onClick={() => removeFormerRelation(index)}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Hubungan"
                    value={relation.relationship}
                    onChange={(e) => updateFormerRelation(index, 'relationship', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Keterangan"
                    value={relation.notes}
                    onChange={(e) => updateFormerRelation(index, 'notes', e.target.value)}
                  />
                </Grid>
              </Grid>
            </Card>
          ))}
        </Box>

        {/* Guarantors */}
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Penjamin
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={addGuarantor}
              startIcon={<AddIcon />}
              sx={{ borderRadius: 2 }}
            >
              Tambah Penjamin
            </Button>
          </Box>
          
          {employeeFormData.staticData.guarantors.map((guarantor, index) => (
            <Card key={index} sx={{ mb: 2, p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Nama"
                    value={guarantor.name}
                    onChange={(e) => updateGuarantor(index, 'name', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Usia"
                    type="number"
                    value={guarantor.age}
                    onChange={(e) => updateGuarantor(index, 'age', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Alamat"
                    value={guarantor.address}
                    onChange={(e) => updateGuarantor(index, 'address', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Telepon"
                    value={guarantor.phone}
                    onChange={(e) => updateGuarantor(index, 'phone', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <IconButton
                    color="error"
                    onClick={() => removeGuarantor(index)}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Hubungan"
                    value={guarantor.relationship}
                    onChange={(e) => updateGuarantor(index, 'relationship', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Keterangan"
                    value={guarantor.notes}
                    onChange={(e) => updateGuarantor(index, 'notes', e.target.value)}
                  />
                </Grid>
              </Grid>
            </Card>
          ))}
        </Box>
      </CardContent>
    </Card>
  );

  const renderStepContent = (step) => {
    // Simple mode
    if (tabValue === 0) {
      switch (step) {
        case 0:
          return renderSimpleEmployeeIdForm();
        case 1:
          return renderSimpleNameForm();
        case 2:
          return renderSimplePersonalInfoForm();
        case 3:
          return renderSimpleWorkInfoForm();
        case 4:
          return renderSimpleBenefitForm();
        default:
          return null;
      }
    }
    
    // Full mode
    switch (step) {
      case 0:
        return renderPersonalDataForm();
      case 1:
        return renderWorkInformationForm();
      case 2:
        return renderEducationLocationForm();
      case 3:
        return renderAdditionalInfoForm();
      case 4:
        return renderCandidateHistoryForm();
      case 5:
        return renderCandidateFamilyForm();
      case 6:
        return renderCandidateSkillsForm();
      case 7:
        return renderCandidateHealthForm();
      case 8:
        return renderEducationForm();
      case 9:
        return renderWorkHistoryForm();
      case 10:
        return renderCoursesForm();
      case 11:
        return renderRelationsForm();
      default:
        return null;
    }
  };
  
  return (
    <>
    <Box sx={{ p: 2 }} className="animate-fade-in">
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton 
            onClick={() => navigate('/kontak/employee')}
            sx={{ 
              bgcolor: 'grey.100',
              '&:hover': { bgcolor: 'grey.200' }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Tambah Karyawan Baru
          </Typography>
        </Box>

        {/* Tabs for Simple/Full Mode */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            centered
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Simple" />
            <Tab label="Full" />
          </Tabs>
        </Paper>

        {/* Stepper */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* Form Content */}
        <Box sx={{ mb: 3 }}>
          {renderStepContent(activeStep)}
        </Box>

        {/* Navigation Buttons */}
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              variant="outlined"
              size="large"
            >
              Kembali
            </Button>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleSaveEmployee}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  disabled={loading}
                  size="large"
                  sx={{ 
                    px: 4,
                    py: 1.5,
                    fontWeight: 600
                  }}
                >
                  {loading ? 'Menyimpan...' : 'Simpan Karyawan'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  size="large"
                  sx={{ 
                    px: 4,
                    py: 1.5,
                    fontWeight: 600
                  }}
                >
                  Selanjutnya
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Add Department Dialog */}
      <Dialog open={openAddDepartmentDialog} onClose={() => setOpenAddDepartmentDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Tambah Departemen</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Kode Departemen"
            fullWidth
            value={newDepartmentCode}
            onChange={(e) => setNewDepartmentCode(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Nama Departemen"
            fullWidth
            value={newDepartmentName}
            onChange={(e) => setNewDepartmentName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDepartmentDialog(false)}>Batal</Button>
          <Button onClick={handleQuickAddDepartment} variant="contained">Simpan</Button>
        </DialogActions>
      </Dialog>

      {/* Add Designation Dialog */}
      <Dialog open={openAddDesignationDialog} onClose={() => setOpenAddDesignationDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Tambah Posisi</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Kode Posisi"
            fullWidth
            value={newDesignationCode}
            onChange={(e) => setNewDesignationCode(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Nama Posisi"
            fullWidth
            value={newDesignationName}
            onChange={(e) => setNewDesignationName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDesignationDialog(false)}>Batal</Button>
          <Button onClick={handleQuickAddDesignation} variant="contained">Simpan</Button>
        </DialogActions>
      </Dialog>

      {/* Add Job Level Dialog */}
      <Dialog open={openAddJoblevelDialog} onClose={() => setOpenAddJoblevelDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Tambah Level Pekerjaan</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Kode Level"
            fullWidth
            value={newJoblevelCode}
            onChange={(e) => setNewJoblevelCode(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Nama Level"
            fullWidth
            value={newJoblevelName}
            onChange={(e) => setNewJoblevelName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddJoblevelDialog(false)}>Batal</Button>
          <Button onClick={handleQuickAddJoblevel} variant="contained">Simpan</Button>
        </DialogActions>
      </Dialog>

      {/* Add Business Unit Dialog */}
      <Dialog open={openAddBuDialog} onClose={() => setOpenAddBuDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Tambah Unit Bisnis</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Kode Unit Bisnis"
            fullWidth
            value={newBuCode}
            onChange={(e) => setNewBuCode(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Nama Unit Bisnis"
            fullWidth
            value={newBuName}
            onChange={(e) => setNewBuName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddBuDialog(false)}>Batal</Button>
          <Button onClick={handleQuickAddBu} variant="contained">Simpan</Button>
        </DialogActions>
      </Dialog>

      {/* Add Office Dialog */}
      <Dialog open={openAddOfficeDialog} onClose={() => setOpenAddOfficeDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Tambah Kantor</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Kode Kantor"
            fullWidth
            value={newOfficeCode}
            onChange={(e) => setNewOfficeCode(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Nama Kantor"
            fullWidth
            value={newOfficeName}
            onChange={(e) => setNewOfficeName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddOfficeDialog(false)}>Batal</Button>
          <Button onClick={handleQuickAddOffice} variant="contained">Simpan</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddEmployee;
